import moment from 'moment';
import _ from 'lodash';
import axios from 'axios';
import {
  trackBookingEvent,
  trackBookingConfirmed,
  funnelParams,
  splitPractitioner,
  practitionerNameFromProvider,
  trackFormFieldErrors,
  bookingCitySlug,
} from './tracking';

console.log('[NK] actions.js v2 loaded');

export default {
  trackBookingButtonClick({ state }, { mode, source }) {
    // Best-effort telemetry; never block booking on failures.
    try {
      const activityId =
        state.activities && state.activities.selected
          ? state.activities.selected.activity_id
          : '';
      const terapeutId =
        state.selectedSlot && state.selectedSlot.terapeut_id
          ? state.selectedSlot.terapeut_id
          : '';

      const url = '/wp-json/nk/track/bookingClick/';
      const payload = {
        mode: mode || '',
        source: source || 'ui',
        activityId: activityId,
        terapeutId: terapeutId,
      };

      // Use sendBeacon when possible so the event survives app-switches (BankID / in-app browsers).
      // This telemetry must never block booking.
      try {
        if (navigator && typeof navigator.sendBeacon === 'function') {
          const body = new Blob([JSON.stringify(payload)], {
            type: 'application/json',
          });
          const ok = navigator.sendBeacon(url, body);
          if (ok) return;
        }
      } catch (e) {
        // fall through
      }

      // Fallback: fetch with keepalive (may still be dropped, but better than a normal XHR in some webviews).
      try {
        if (typeof fetch === 'function') {
          fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            keepalive: true,
            credentials: 'same-origin',
          }).catch(() => {});
          return;
        }
      } catch (e) {
        // fall through
      }

      // Last resort: axios.
      axios.post(url, payload);
    } catch (e) {
      // ignore
    }
  },
  init({ state, dispatch, commit }, payload) {
    state.vueInstance = payload.vueInstance;
    dispatch('setupDefaultDates');

    // Initialize cities for booking.
    // UX requirement: city starts unselected when there are multiple cities; dependent controls are disabled
    // until the user explicitly chooses a city. If there is only one city, auto-select it.
    state.citiesReady = dispatch('getAllAvailableCities').then((cities) => {
      if (!state.cities || state.cities.selected) return cities;
      if (Array.isArray(cities) && cities.length === 1) {
        dispatch('setSelectedCity', { city: cities[0], programmatic: true });
      } else if (
        Array.isArray(cities) && cities.length > 1 &&
        state.activityIdFromUrl && window.activity_id
      ) {
        // Coming from price list with a specific treatment:
        // filter the city dropdown to only show cities that offer this treatment.
        dispatch('filterCitiesByPendingActivity', window.activity_id);
      }
      return cities;
    });

    /* If we already have a default activity id, we don't need to get all the activity groups */
    if (!window.activity_id) {
      state.activities.initialLoad = false;
      dispatch('getActivityGroups');
    } else {
      /* Treatment pages don't use the groups UI, but tracking needs the
         group id->name map so funnelParams can resolve service_category.
         namesOnly leaves activityGroups.available empty on purpose:
         populating it here would race the auto-group-select _.find in
         getActivities and nondeterministically change page behavior. */
      dispatch('getActivityGroups', { namesOnly: true });
    }

    /* Check if there's a query parameter set for the activity id */
    var uri = window.location.search.substring(1);
    var params = new URLSearchParams(uri);
    if (params.get('activity_id')) {
      window.activity_id = params.get('activity_id');
      state.activityIdFromUrl = true;
      // Don't set modalState = 3 or toggleBodyClass here.
      // We need to wait for the user to select a city first (when multiple cities exist).
      // The activity will be applied via applyPendingUrlActivity after city selection.
    }

    dispatch('getActivities');

    dispatch('getCustomTreatmentDurations');

    dispatch('maybeLoadBookingFromOrderRef');
  },

  // City-related actions
  getAllAvailableCities({ commit }) {
    return new Promise((resolve) => {
      commit('SET_CITIES_LOADED', false);

      // Fallback if admin config isn't filled in yet.
      const fallback = [
        {
          id: 'stockholm',
          name: 'Stockholm',
          slug: 'stockholm',
          locations: [
            { id: '1', name: 'Drottninggatan 33', short_name: 'Drottninggatan 33' },
          ],
        },
        {
          id: 'goteborg',
          name: 'Göteborg',
          slug: 'goteborg',
          locations: [{ id: '6', name: 'Stampgatan 4', short_name: 'Stampgatan 4' }],
        },
      ];

      // Preferred source of truth: Platser (profet_location) grouped by city
      axios
        .get('/wp-json/nk/getBookingLocations/')
        .then((resp) => {
          const rows = resp && resp.data && resp.data.data ? resp.data.data : [];
          if (Array.isArray(rows)) {
            // Important: empty array is a valid response (e.g. no enabled locations or all clinics are draft).
            // In that case we should NOT show fallback cities.
            commit('SET_AVAILABLE_CITIES', rows);
            commit('SET_CITIES_LOADED', true);
            resolve(rows);
            return;
          }
          // If response shape is unexpected, fall back (helps during deploy mismatch).
          commit('SET_AVAILABLE_CITIES', fallback);
          commit('SET_CITIES_LOADED', true);
          resolve(fallback);
        })
        .catch(() => {
          commit('SET_AVAILABLE_CITIES', fallback);
          commit('SET_CITIES_LOADED', true);
          resolve(fallback);
        });
    });
  },

  /*
    Payload is either the city itself (user picked one in the UI — tracked)
    or { city, programmatic: true } for auto-selections (single-city
    auto-select, preloads) which must NOT fire booking_city_changed — the
    spec defines it as the USER changing city.
  */
  setSelectedCity({ state, commit, dispatch }, payload) {
    const programmatic = !!(payload && payload.programmatic === true);
    const city = programmatic ? payload.city : payload;
    // Re-selecting the city that is already selected is a no-op: no tracking
    // events, and the user's treatment/time selections are kept.
    if (
      !programmatic &&
      city &&
      state.cities.selected &&
      state.cities.selected.id === city.id
    ) {
      return;
    }
    const cityFrom =
      state.cities && state.cities.selected ? state.cities.selected.name : null;
    const cityTo = city ? city.name : null;
    if (!programmatic && cityFrom !== cityTo) {
      trackBookingEvent('booking_city_changed', {
        city_from: cityFrom,
        city_to: cityTo,
      });
    }
    if (!programmatic && city) {
      /* booking_clinic_selected: fired when the user selects a city
         (not when re-selecting the one already selected), telling which
         clinic the choice maps to. clinic_location mirrors booking_timeslot_selected: the admin-
         configured location label (usually a street address) when the city
         has exactly one clinic, else the city name — the same fallback the
         timeslot event uses when no location can be resolved. */
      const cityLocations = Array.isArray(city.locations)
        ? city.locations
        : [];
      const singleLocation =
        cityLocations.length === 1 ? cityLocations[0] : null;
      trackBookingEvent('booking_clinic_selected', {
        city: cityTo,
        clinic_location:
          (singleLocation &&
            (singleLocation.name || singleLocation.short_name)) ||
          cityTo,
      });
    }
    commit('SET_SELECTED_CITY', city);
    /* A real city selection outranks the server fallback — drop it so it can
       never resolve a later booking (see resolveBookingCity). */
    state.booking.cityFromServer = null;
    commit('RESET_SELECTIONS_FOR_CITY_CHANGE');
    // Mobile UX: if the user changes city while viewing the booking schedule/timeslots,
    // they must start over (close the modal/schedule view).
    dispatch('setModalState', 0);

    // Set available locations for this city from admin config
    state.locations.available = (city && Array.isArray(city.locations)) ? city.locations : [];
    // Auto-select if only one location; otherwise keep unselected and show location picker when >1
    state.locations.selected =
      state.locations.available.length === 1 ? state.locations.available[0] : false;

    // Reset city-filter results immediately so we don't briefly show the previous city's filtered groups.
    // fetchCityBookableActivityGroups will repopulate (or keep null if not ready).
    state.activityGroups.cityBookableGroupIds = null;

    // City change => reset cached activity availability and cancel any in-flight probes
    state.activities.cityAvailabilityCache = {};
    state.activities.cityBookableIds = null;
    if (state.ui.loading.activitiesForCitySignal) {
      try {
        state.ui.loading.activitiesForCitySignal.cancel('cancel activitiesForCity (city changed)');
      } catch (e) {}
      state.ui.loading.activitiesForCitySignal = null;
    }
    state.ui.loading.activitiesForCity = false;

    dispatch('hideEmptyActivityGroups');
    dispatch('fetchCityBookableActivityGroups');

    // If the user came from the price list with ?activity_id=X, apply the pending activity now.
    dispatch('applyPendingUrlActivity');
  },

  /**
   * Apply a pending activity from the URL (?activity_id=X) after the user selects a city.
   * This handles the edge case where the user navigates from the price list and hasn't picked a city yet.
   */
  applyPendingUrlActivity({ state, dispatch }) {
    if (!state.pendingUrlActivity) return;

    const { activity, activityGroup } = state.pendingUrlActivity;
    state.pendingUrlActivity = null; // Clear so it only fires once.

    if (activityGroup) {
      dispatch('setActivityGroup', { activityGroup, programmatic: true });
    }
    dispatch('setActivity', { activity, programmatic: true });
    dispatch('toggleBodyClass');
    state.ui.modalState = 3;
  },

  /**
   * When navigating from the price list with ?activity_id, check which cities
   * actually offer that treatment and hide the rest from the dropdown.
   * Uses the same bookable-groups endpoint that fetchCityBookableActivityGroups uses.
   */
  filterCitiesByPendingActivity({ state, dispatch }, activityId) {
    if (!activityId || !state.cities.available || state.cities.available.length <= 1) return;

    state.cities.filteringByActivity = true;
    const cities = state.cities.available;
    const checks = cities.map((city) => {
      const locationIds = Array.isArray(city.locations)
        ? city.locations.map((l) => String(l.id)).filter(Boolean)
        : [];

      if (!locationIds.length) return Promise.resolve({ city, available: false });

      return axios
        .post('/wp-json/nk/cb/getBookableActivityGroupIdsForLocations/', {
          location_ids: locationIds,
          days: 90,
        })
        .then((resp) => {
          const payload = resp && resp.data && resp.data.data ? resp.data.data : null;
          if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
            if (!payload.ready) {
              return { city, available: true }; // Cache warming – keep city (safe fallback).
            }
            const hasField = Object.prototype.hasOwnProperty.call(payload, 'activity_ids');
            if (!hasField) {
              return { city, available: true }; // Old cache format without activity_ids – keep city.
            }
            const actIds = Array.isArray(payload.activity_ids) ? payload.activity_ids : [];
            return {
              city,
              available: actIds.map(String).includes(String(activityId)),
            };
          }
          return { city, available: true }; // Unknown format – keep city.
        })
        .catch(() => ({ city, available: true })); // Error – keep city.
    });

    Promise.all(checks).then((results) => {
      const available = results.filter((r) => r.available).map((r) => r.city);

      // Only apply filter if at least one city passed; otherwise show all as fallback.
      if (available.length > 0 && available.length < cities.length) {
        state.cities.available = available;
      }

      state.cities.filteringByActivity = false;

      // If exactly one city remains (or filtering narrowed to one), auto-select.
      if (state.cities.available.length === 1 && !state.cities.selected) {
        dispatch('setSelectedCity', {
          city: state.cities.available[0],
          programmatic: true,
        });
      }
    }).catch(() => {
      state.cities.filteringByActivity = false;
    });
  },

  clearCitySelection({ state, commit, dispatch }) {
    const cityFrom =
      state.cities && state.cities.selected ? state.cities.selected.name : null;
    commit('CLEAR_CITY_SELECTION');
    commit('RESET_SELECTIONS_FOR_CITY_CHANGE');
    const stockholm =
      (state.cities.available || []).find(
        (c) => (c.slug || '').toLowerCase() === 'stockholm'
      ) || { id: 'stockholm', name: 'Stockholm', slug: 'stockholm', locations: [{ id: '1', name: 'Drottninggatan 33', short_name: 'Drottninggatan 33' }] };
    commit('SET_SELECTED_CITY', stockholm);
    if (cityFrom !== stockholm.name) {
      trackBookingEvent('booking_city_changed', {
        city_from: cityFrom,
        city_to: stockholm.name,
      });
    }
    state.locations.available = (stockholm && Array.isArray(stockholm.locations)) ? stockholm.locations : [];
    state.locations.selected =
      state.locations.available.length === 1 ? state.locations.available[0] : false;

    // Reset city-filter results immediately so we don't briefly show the previous city's filtered groups.
    state.activityGroups.cityBookableGroupIds = null;

    // Reset cached activity availability and cancel any in-flight probes
    state.activities.cityAvailabilityCache = {};
    state.activities.cityBookableIds = null;
    if (state.ui.loading.activitiesForCitySignal) {
      try {
        state.ui.loading.activitiesForCitySignal.cancel('cancel activitiesForCity (city cleared)');
      } catch (e) {}
      state.ui.loading.activitiesForCitySignal = null;
    }
    state.ui.loading.activitiesForCity = false;

    dispatch('hideEmptyActivityGroups');
    dispatch('fetchCityBookableActivityGroups');
  },

  fetchCityBookableActivityGroups({ state, dispatch }, opts = {}) {
    return new Promise((resolve) => {
      const attempt = opts && opts.attempt ? Number(opts.attempt) : 0;
      // Computing / warming the cache can take longer than a few seconds (especially after a cache-version bump).
      // We retry longer to avoid ending up in "klar • inte aktivt (visar alla)" forever.
      const maxAttempts = 40;
      const retryDelayMs = 2000;
      let keepLoading = false;

      // Default: show all groups (null means "no filtering applied yet")
      if (!state.cities || !state.cities.selected) {
        state.activityGroups.cityBookableGroupIds = null;
        state.ui.loading.activityGroupsForCity = false;
        resolve(null);
        return;
      }

      const city = state.cities.selected;
      const locationIds = Array.isArray(city.locations)
        ? city.locations.map((l) => String(l.id)).filter(Boolean)
        : [];

      const cooldownKey = `${city.slug || city.id || ''}|${locationIds.join(',')}|90`;
      const now = Date.now();
      if (
        attempt === 0 &&
        state.ui &&
        state.ui.loading &&
        state.ui.loading.activityGroupsForCityCooldownKey === cooldownKey &&
        Number(state.ui.loading.activityGroupsForCityCooldownUntil || 0) > now
      ) {
        // Avoid repeated immediate re-requests (e.g. from re-renders) while warming is in progress.
        state.activityGroups.cityBookableGroupIds = null;
        state.ui.loading.activityGroupsForCity = false;
        resolve(null);
        return;
      }

      if (!locationIds.length) {
        state.activityGroups.cityBookableGroupIds = [];
        state.ui.loading.activityGroupsForCity = false;
        resolve([]);
        return;
      }

      // IMPORTANT: Do not block the UI while cache is warming.
      // We'll retry in the background without showing a spinner.
      state.ui.loading.activityGroupsForCity = false;
      // While loading, keep previous value to avoid flicker; UI should not blank.

      axios
        .post('/wp-json/nk/cb/getBookableActivityGroupIdsForLocations/', {
          location_ids: locationIds,
          days: 90,
        })
        .then((resp) => {
          const payload = resp && resp.data && resp.data.data ? resp.data.data : null;

          // New backend format: { ready: boolean, group_ids: [] }
          if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
            const ready = !!payload.ready;
            const ids = Array.isArray(payload.group_ids) ? payload.group_ids : [];
            const hasActivityIdsField = Object.prototype.hasOwnProperty.call(payload, 'activity_ids');
            const activityIds = Array.isArray(payload.activity_ids) ? payload.activity_ids : [];

            if (!ready) {
              // Cache is warming: DO NOT apply filtering yet, keep list visible.
              // Retry a few times with a small delay (in background; no spinner).
              state.activityGroups.cityBookableGroupIds = null;
              state.activities.cityBookableIds = null;
              if (attempt < maxAttempts) {
                keepLoading = true;
                // Cooldown prevents extra attempt=0 calls from hammering while we retry.
                state.ui.loading.activityGroupsForCityCooldownKey = cooldownKey;
                state.ui.loading.activityGroupsForCityCooldownUntil = Date.now() + retryDelayMs - 50;
                setTimeout(() => {
                  dispatch('fetchCityBookableActivityGroups', { attempt: attempt + 1 });
                }, retryDelayMs);
              } else {
                // Bail out: stop spinning forever; fall back to showing all groups.
                keepLoading = false;
                // After giving up, cool down for a while to avoid looping forever.
                state.ui.loading.activityGroupsForCityCooldownKey = cooldownKey;
                state.ui.loading.activityGroupsForCityCooldownUntil = Date.now() + 5 * 60 * 1000;
              }
              // Never block UI while warming
              state.ui.loading.activityGroupsForCity = false;
              resolve(null);
              return;
            }

            // Filter out groups that have zero activities in local state (stale backend cache).
            const allActivities = state.activities && Array.isArray(state.activities.available)
              ? state.activities.available
              : [];
            const verifiedIds = ids.filter((gid) => {
              const gidStr = String(gid);
              return allActivities.some(
                (a) =>
                  a &&
                  String(a.activity_group_id) === gidStr &&
                  String(a.activity_active) === '1' &&
                  String(a.activity_hide_in_patient_portal || '') !== '1'
              );
            });

            state.activityGroups.cityBookableGroupIds = verifiedIds;
            // If activity_ids is missing entirely, this is an older cache payload.
            // Treat it as "pending" so global search can fall back to probing instead of incorrectly showing 0 results.
            // Also treat "empty activity_ids but non-empty group_ids" as pending, since that means we're on an old/stale cache
            // (or a background recompute is still in progress) and we must not interpret it as "0 bookable activities".
            state.activities.cityBookableIds =
              !hasActivityIdsField || (verifiedIds.length > 0 && activityIds.length === 0)
                ? null
                : activityIds;
            // Cache ready => clear cooldown
            state.ui.loading.activityGroupsForCityCooldownKey = null;
            state.ui.loading.activityGroupsForCityCooldownUntil = 0;
            resolve(verifiedIds);
            return;
          }

          // Back-compat: old backend returned an array directly
          const ids = Array.isArray(payload) ? payload : [];
          state.activityGroups.cityBookableGroupIds = ids;
          state.activities.cityBookableIds = null;
          resolve(ids);
        })
        .catch(() => {
          // On error, fall back to showing all groups
          state.activityGroups.cityBookableGroupIds = null;
          resolve(null);
        })
        .finally(() => {
          // Never keep spinner running while warming/retrying
          state.ui.loading.activityGroupsForCity = false;
        });
    });
  },

  /* 
    When the booking module is first loaded, check if a query parameter for "orderref" exists. 
    If it does, load the order with the order ref and start polling it for a BankID response. 
    This is done because the BankID app sometimes redirects the user to a new browser or new tab after the user has signed with BankID, 
    and in those scenarios we need to load the order from using the OrderRef since we can't access the state of the users booking.
    */
  maybeLoadBookingFromOrderRef({ state, dispatch }) {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    var orderref = params.get('orderref');
    if (!orderref) {
      return;
    }
    /* If there's an order ref, set it up and start fetching the BankID response. */
    state.booking.orderRef = orderref;

    /* Set up loading... */
    state.ui.loading.doingBooking = true;
    state.orderRefQueryParameterLoaded = true;
    /* Set a timeout here to make all variables work correctly */
    window.setTimeout(() => {
      /* Wait for the city list before confirming. This tab is a fresh page
         load with no city selected, so the booking city can only be
         recovered by looking the slot's location_id up in cities.available
         (resolveBookingCity) — and the revisit branch answers immediately,
         which would otherwise race the city request and push
         booking_confirmed without a city. Never block the booking itself:
         a rejected or missing promise still confirms. */
      const ready =
        state.citiesReady && typeof state.citiesReady.then === 'function'
          ? state.citiesReady
          : Promise.resolve();
      /* Cap the wait. getAllAvailableCities never REJECTS (its catch resolves
         a fallback) and axios sets no timeout here, so a stalled
         getBookingLocations request leaves this promise pending for the
         browser's connection timeout — minutes. On this path the dispatch is
         what PLACES the booking, so a hung city request must never hold it:
         a missing city is a tracking gap, a missing booking is not. */
      const CITY_WAIT_MS = 2000;
      Promise.race([
        ready.catch(() => {}),
        new Promise((resolve) => window.setTimeout(resolve, CITY_WAIT_MS)),
      ]).then(() => {
        dispatch('collectBankIDResponseAndSendBooking');
      });
    }, 1000);
  },

  setupDefaultDates({ state, dispatch }, getAvailableTimes) {
    /* If we're on a Sunday, set the current date to Monday next week */
    let date = moment();
    /*
        if (moment().format('e') == '6') {
          date.add(1, 'd');
        }
        */
    state.firstLoadForActivity = true;
    dispatch('setupActiveDateFromDate', {
      date: date.format('YYYY-MM-DD'),
    }).then(() => {
      /* Clone todays date and set it to the active date */
      state.dates.current = Object.assign({}, state.dates.active);

      /* Set the last future date that we will fetch bookings for. By default, we set this to 6 months */
      state.dates.current.checkBookingsToDate = moment()
        .add(1, 'months')
        .endOf('isoWeek')
        .format('YYYY-MM-DD');

      if (getAvailableTimes) {
        var payload = {
          dontNavigateToNextAvailableTime: true, // Don't trigger navigation to the next available time because it causes the user to be thrown back into the calendar.
        };
        dispatch('getAvailableTimes', payload);
      }
    });
  },
  setupWeekdaysForWeek({ state, dispatch }, weekNumber) {
    dispatch('setupDatesForMonth');
    var weekdays = [
      'Måndag',
      'Tisdag',
      'Onsdag',
      'Torsdag',
      'Fredag',
      'Lördag',
      'Söndag',
    ];
    state.dates.active.weekdays = [];
    for (var i in weekdays) {
      var data = {
        date: moment()
          .isoWeekYear(state.dates.active.year)
          .week(weekNumber)
          .isoWeekday(weekdays[i]),
        name: moment()
          .isoWeekYear(state.dates.active.year)
          .week(weekNumber)
          .isoWeekday(weekdays[i])
          .format('ddd'),
        longName: moment()
          .isoWeekYear(state.dates.active.year)
          .week(weekNumber)
          .isoWeekday(weekdays[i])
          .format('dddd'),
        dayOfMonth: moment()
          .isoWeekYear(state.dates.active.year)
          .week(weekNumber)
          .isoWeekday(weekdays[i])
          .format('D'),
        niceDate: moment()
          .isoWeekYear(state.dates.active.year)
          .week(weekNumber)
          .isoWeekday(weekdays[i])
          .format('YYYY-MM-DD'),
        monthShortName: moment()
          .isoWeekYear(state.dates.active.year)
          .week(weekNumber)
          .isoWeekday(weekdays[i])
          .format('MMM'),
        monthLongName: moment()
          .isoWeekYear(state.dates.active.year)
          .week(weekNumber)
          .isoWeekday(weekdays[i])
          .format('MMMM'),
        availableTimes: [],
        activeTimes: [],
      };
      state.dates.active.weekdays.push(data);
    }
    dispatch('fillOutCalendarWithAvailableTimes');
  },
  setupDatesForMonth({ state, dispatch }) {
    /*
        Get all the dates that should be displayed in the calendar.
        I.e. even if the month starts on the 1st of March, we might want to display a few days before or after
        in order to present a full and coherent calendar
        */
    let firstDayOfMonth = moment(state.dates.active.date.format('YYYY-MM-01'));
    let lastDayOfMonth = moment(
      state.dates.active.date.format('YYYY-MM-') +
        state.dates.active.date.daysInMonth()
    );

    /* 2020-10-15: Trying to set up datesInMonth a different way since the old way caused some issues when viewing January 2020 in month view */

    /*
        let datesInMonth = [];
        for (let weekNumber = firstDayOfMonth.isoWeek(); weekNumber <= lastDayOfMonth.isoWeek(); weekNumber++) {
          for (let dayOfWeek = 1; dayOfWeek <= 7; dayOfWeek++) {
            let date = moment()
              .isoWeekYear(state.dates.active.year)
              .week(weekNumber)
              .isoWeekday(dayOfWeek)
              .format('YYYY-MM-DD');
            datesInMonth.push(date);
          }
        }
        */

    /* We'll fill this empty array with all the dates for this month */
    let datesInMonth = [];

    /* Add dates from previous month to the start of the calendar so that the calendar is nice and even if the new month doesn't start on a Monday */
    let datesToAddToStartOfMonth = firstDayOfMonth.isoWeekday();
    if (datesToAddToStartOfMonth) {
      let dates = []; // We'll flip this array later to make the dates appear in the correct order */
      for (i = 1; i < datesToAddToStartOfMonth; i++) {
        let date = firstDayOfMonth.clone().subtract(i, 'days');
        dates.push(date.format('YYYY-MM-DD'));
      }
      /* Flipping the dates array to make the dates appear in the correct order */
      dates.reverse();
      for (let i in dates) {
        datesInMonth.push(dates[i]);
      }
    }

    /* Set up the rest of the days in the month */
    var currentMonth = moment(state.dates.active.date).month() + 1;
    var currentYear = moment(state.dates.active.date).year();
    var numberOfDays = moment(state.dates.active.date).daysInMonth();
    for (var i = 1; i <= numberOfDays; i++) {
      if (i < 10) {
        i = '0' + i.toString();
      }
      let formatedMonth = currentMonth;
      if (currentMonth < 10) {
        formatedMonth = '0' + currentMonth.toString();
      }
      var dateString = currentYear + '-' + formatedMonth + '-' + i;
      datesInMonth.push(moment(dateString));
    }

    /* Add dates from upcoming month to the end of the calendar so that the calendar is nice and even if the new month doesn't end on a Sunday */
    let datesToAddToEndOfMonth = 7 - lastDayOfMonth.isoWeekday();
    if (datesToAddToEndOfMonth) {
      for (i = 1; i <= datesToAddToEndOfMonth; i++) {
        let date = lastDayOfMonth.clone().add(i, 'days');
        datesInMonth.push(date);
      }
    }

    state.dates.active.monthDates = [];

    /* Loop over all dates in the month */
    for (let i in datesInMonth) {
      let date = moment(datesInMonth[i]);
      var data = {
        date: date,
        niceDate: date.format('YYYY-MM-DD'),
        dayOfWeek: date.isoWeekday(),
        dayOfMonth: date.date(),
        weekOfYear: date.isoWeek(),
        availableTimes: [],
        activeTimes: [],
      };
      state.dates.active.monthDates.push(data);
    }
  },
  fillOutCalendarWithAvailableTimes({ state, dispatch }) {
    if (!state.availableTimes.length) {
      //return;
    }

    /* Order available times by time */
    state.availableTimes = _.sortBy(state.availableTimes, (o) => o.timestamp);

    return dispatch('fillOutDates');
  },
  fillOutDates({ state, getters }) {
    return new Promise(
      (resolve) => {
        const norm = (s) =>
          String(s || '')
            .toLowerCase()
            .replace(/\s+/g, ' ')
            .trim();
        const selectedProviderRaw =
          state.providers && state.providers.selected != null
            ? String(state.providers.selected).trim()
            : '';
        const selectedProvider = norm(selectedProviderRaw);
        const selectedProviderId =
          getters && getters.selectedProviderId != null
            ? String(getters.selectedProviderId).trim()
            : '';
        const providerMatches = (time) => {
          if (!selectedProviderRaw) return true;
          if (selectedProviderId) {
            return String(time && time.terapeut_id != null ? time.terapeut_id : '').trim() === selectedProviderId;
          }
          const sign = norm(time && time.terapeut_sign != null ? time.terapeut_sign : '');
          if (!sign || !selectedProvider) return false;
          return sign.includes(selectedProvider) || selectedProvider.includes(sign);
        };
        const cityFallbackLabel =
          state.cities && state.cities.selected && state.cities.selected.name
            ? state.cities.selected.name
            : 'Okänd plats';
        const locationLabelById =
          state.locations && Array.isArray(state.locations.available)
            ? state.locations.available.reduce((acc, l) => {
                acc[String(l.id)] = l;
                return acc;
              }, {})
            : {};
        /* Set up available times */
        let locationIds = [];
        for (var i in state.dates.active.weekdays) {
          var weekday = state.dates.active.weekdays[i];
          weekday.availableTimes = [];
          weekday.activeTimes = [];
          for (var x in state.availableTimes) {
            var time = {
              ...state.availableTimes[x],
              location_name:
                (locationLabelById[String(state.availableTimes[x].location_id)] &&
                  (locationLabelById[String(state.availableTimes[x].location_id)].name ||
                    locationLabelById[String(state.availableTimes[x].location_id)].short_name)) ||
                state.availableTimes[x].location_name ||
                cityFallbackLabel,
              // Calendar UX: always show CITY in the slot tiles.
              // The detailed address is shown in the booking summary box via setSelectedSlot().
              location_short_name: cityFallbackLabel,
            };
            locationIds.push(time.location_id);
            if (time.date == weekday.niceDate) {
              //if (time.terapeut_id == 8 || time.terapeut_id == 12) {
              /*
                            if (time.terapeut_id == 12) {
                              continue;
                            }
                            */

              if (!state.locations.selected) {
                // weekday.availableTimes.push(time);
              }

              /* If we should display times for all providers, push the time slot into the activeTimes array */
              // if (!state.providers.selected && !state.locations.selected) {
              weekday.activeTimes.push(time);
              // }

              /* If we should NOT display times for all providers, push the time slot into the activeTimes array only if the provider name matches the filtered name*/
              // if (
              //   state.providers.selected &&
              //   time.terapeut_sign == state.providers.selected
              // ) {
              //   // console.log('ADD:X ', time.location_id);
              //   weekday.activeTimes.push(time);
              // }
              // if (state.locations.selected && time.location_id == state.locations.selected.id) {
              //   // console.log('ADD:D ', time.location_id);
              //   weekday.activeTimes.push(time);
              // }
            }
          }
          state.dates.active.weekdays[i].availableTimes =
            weekday.availableTimes;
          if (state.providers.selected && state.locations.selected) {
            state.dates.active.weekdays[i].activeTimes =
              weekday.activeTimes.filter(
                (x) =>
                  String(x.location_id) === String(state.locations.selected.id) &&
                  providerMatches(x)
              );
          } else if (state.providers.selected && !state.locations.selected) {
            state.dates.active.weekdays[i].activeTimes =
              weekday.activeTimes.filter(
                (x) => providerMatches(x)
              );
          } else if (!state.providers.selected && state.locations.selected) {
            state.dates.active.weekdays[i].activeTimes =
              weekday.activeTimes.filter(
                (x) => String(x.location_id) === String(state.locations.selected.id)
              );
          } else {
            state.dates.active.weekdays[i].activeTimes = weekday.activeTimes;
          }
        }
        for (let i in state.dates.active.monthDates) {
          let monthDate = state.dates.active.monthDates[i];
          monthDate.availableTimes = [];
          monthDate.activeTimes = [];
          for (var x in state.availableTimes) {
            var time = state.availableTimes[x];

            if (time.date == monthDate.niceDate) {
              //if (time.terapeut_id == 8 || time.terapeut_id == 12) {
              /*
                            if (time.terapeut_id == 12) {
                              continue;
                            }
                            */

              monthDate.availableTimes.push(time);

              /* If we should display times for all providers, push the time slot into the activeTimes array */
              if (!state.providers.selected) {
                monthDate.activeTimes.push(time);
              }

              /* If we should NOT display times for all providers, push the time slot into the activeTimes array only if the provider name matches the filtered name*/
              if (
                state.providers.selected &&
                providerMatches(time) &&
                !state.locations.selected
              ) {
                monthDate.activeTimes.push(time);
              }
              if (
                state.locations.selected &&
                time.location_id == state.locations.selected.id
              ) {
                monthDate.activeTimes.push(time);
              }
            }
          }
          state.dates.active.monthDates[i].availableTimes =
            monthDate.availableTimes;
          state.dates.active.monthDates[i].activeTimes = monthDate.activeTimes;
        }
        resolve();
      },
      (reject) => {
        reject();
      }
    );
  },
  navigateToNextAvailableTime({ state, dispatch, getters }) {
    /*
        If we're on the week view, check if there are any available times for the current week.
        If not, navigate to the next week with available times.
        */
    if (state.ui.calendarViewType != 'week') {
      //return;
    }
    let firstDayNextWeek = moment(state.dates.active.weekEndDate).add(
      1,
      'days'
    );
    /*
        for (let i in state.availableTimes) {
          let availableTime = state.availableTimes[i];
          Check if Monday next week is AFTER the time slot time.
          If it is, don't continue this function and don't navigate the user in the calendar.
          if (firstDayNextWeek.isAfter(availableTime.date)) {
            return;
          }
        }
         */
    if (state.availableTimes.length > 0) {
      console.log(7);
      /* If no provider is selected, navigate to the first available time */
      if (!state.providers.selected) {
        dispatch('setupActiveDateFromDate', {
          date: state.availableTimes[0].date,
        });
        return;
      }
      console.log(8);
      /* If a provider is selected, navigate to the first time for that provider */
      const norm = (s) =>
        String(s || '')
          .toLowerCase()
          .replace(/\s+/g, ' ')
          .trim();
      const selectedProviderRaw =
        state.providers && state.providers.selected != null
          ? String(state.providers.selected).trim()
          : '';
      const selectedProvider = norm(selectedProviderRaw);
      // Prefer filtering by provider id when available (more robust than string matching).
      const selectedProviderId =
        getters && getters.selectedProviderId != null
          ? String(getters.selectedProviderId).trim()
          : '';
      for (let i in state.availableTimes) {
        let availableTime = state.availableTimes[i];
        if (
          (selectedProviderId &&
            String(availableTime.terapeut_id || '').trim() === selectedProviderId) ||
          (() => {
            const sign = norm(availableTime && availableTime.terapeut_sign != null ? availableTime.terapeut_sign : '');
            if (!sign || !selectedProvider) return false;
            return sign.includes(selectedProvider) || selectedProvider.includes(sign);
          })()
        ) {
          dispatch('setupActiveDateFromDate', { date: availableTime.date });
          return;
        }
      }
    }
  },
  getActivities({ state, dispatch }) {
    axios.get('/wp-json/nk/cb/getActivitiesFromDB/').then((response) => {
      state.activities.available = [];
      var activities = response.data.data.rows.row;
      for (var i in activities) {
        /* Skip activities with a blank name */
        if (activities[i].activity_name.length == 0) {
          continue;
        }

        state.activities.available.push(activities[i]);

        /* This activity should be displayed, which means we should push the activity group id to an array to keep track of which to show. We can then show/hide the correct acitivty groups, and hide those without any visible activities in them. */

        if (
          activities[i].activity_active == '1' &&
          activities[i].activity_hide_in_patient_portal != '1'
        ) {
          if (
            state.activityGroups.withActivities.indexOf(
              activities[i].activity_group_id
            ) === -1
          ) {
            state.activityGroups.withActivities.push(
              activities[i].activity_group_id
            );
          }
        }

        if (window.activity_id) {
          if (activities[i].activity_id == window.activity_id) {
            /* Set up the activity group ID from the window variable */
            var activityGroupId = activities[i].activity_group_id;
            var activityGroup = _.find(
              state.activityGroups.available,
              function (o) {
                return o.activity_group_id === activityGroupId;
              }
            );

            // If the activity_id came from a URL param (price list link), we need
            // city selection first. If it came from PHP (single treatment page), apply immediately.
            if (!state.activityIdFromUrl) {
              // PHP-set activity_id: apply immediately (original behavior, no modalState change).
              if (activityGroup) {
                dispatch('setActivityGroup', { activityGroup, programmatic: true });
              }
              dispatch('setActivity', { activity: activities[i], programmatic: true });
            } else if (state.cities.selected) {
              // URL activity_id AND city already selected (e.g. single city auto-selected): apply now.
              if (activityGroup) {
                dispatch('setActivityGroup', { activityGroup, programmatic: true });
              }
              dispatch('setActivity', { activity: activities[i], programmatic: true });
              dispatch('toggleBodyClass');
              state.ui.modalState = 3;
            } else {
              // URL activity_id but no city yet – park the matched activity for later.
              state.pendingUrlActivity = {
                activity: activities[i],
                activityGroup: activityGroup || null,
              };
            }
          }
        }
      }

      state.activities.available = _.sortBy(state.activities.available, (o) =>
        parseInt(o.activity_order, 10)
      );

      if (state.initialLoad == false) {
        dispatch('setupActivities');
      }
      state.activities.loaded = true;
      dispatch('hideEmptyActivityGroups');
    });
  },
  getActivityGroups({ state }, payload) {
    const namesOnly = !!(payload && payload.namesOnly);
    axios
      .get('/wp-json/nk/cb/getActivityGroupsFromDB/')
      .then((response) => {
        /* Guarded: the namesOnly variant runs on every treatment-page load,
           so an empty/malformed payload (e.g. the activities option not yet
           warmed on a fresh environment) must degrade silently — funnelParams
           tolerates namesById staying null. */
        var activityGroups =
          response &&
          response.data &&
          response.data.data &&
          response.data.data.rows
            ? response.data.data.rows.row
            : null;
        if (!activityGroups) {
          return;
        }
        /* Always maintain the id->name map — funnelParams uses it to resolve
           service_category on pages where the groups UI (available/loaded)
           is never populated. */
        var namesById = {};
        for (var i in activityGroups) {
          var group = activityGroups[i];
          if (group && group.activity_group_id) {
            namesById[String(group.activity_group_id)] =
              group.activity_group_name;
          }
        }
        state.activityGroups.namesById = namesById;
        if (namesOnly) {
          return;
        }
        state.activityGroups.available = [];
        for (var j in activityGroups) {
          state.activityGroups.available.push(activityGroups[j]);
        }
        state.activityGroups.loaded = true;
      })
      .catch(() => {});
  },
  hideEmptyActivityGroups({ state, dispatch }) {
    if (!state.activityGroups.loaded && !state.activities.loaded) {
      window.setTimeout(() => {
        dispatch('hideEmptyActivityGroups');
      }, 100);
      return;
    }

    // Always rebuild bookable list (city filter may change it)
    state.activityGroups.bookable = [];

    for (var i in state.activityGroups.available) {
      var activityGroupId = state.activityGroups.available[i].activity_group_id;
      if (_.includes(state.activityGroups.withActivities, activityGroupId)) {
        state.activityGroups.bookable.push(state.activityGroups.available[i]);
      }
    }

    state.activityGroups.bookable = _.sortBy(
      state.activityGroups.bookable,
      (o) => parseInt(o.activity_group_order, 10)
    );

    /* If we coulnd't find any bookable activityGroups, try this again in 1 second */
    if (state.activityGroups.bookable.length == 0) {
      window.setTimeout(() => {
        dispatch('hideEmptyActivityGroups');
      }, 100);
    }
  },
  /*
    Payload is either the activityGroup itself (user toggled a filter chip —
    tracked) or { activityGroup, programmatic: true } for page-load flows
    (PHP/URL-preselected treatment) which must NOT fire
    booking_service_filter_changed.
  */
  setActivityGroup({ state, dispatch, getters }, payload) {
    const programmatic = !!(payload && payload.programmatic === true);
    const activityGroup = programmatic ? payload.activityGroup : payload;
    /* Don't allow the user to set this if we're loading times since it'll mess up the UI */
    if (getters.loading.times) {
      // return;
    }

    /* Don't allow a new slot to be set if the user is currently doing a booking, i.e. a BankID sign */
    if (state.ui.loading.doingBooking) {
      return;
    }

    if (state.activityGroups.selected == activityGroup) {
      state.activityGroups.selected = false;
      if (!programmatic) {
        trackBookingEvent('booking_service_filter_changed', {
          filter_action: 'remove',
          service_name: activityGroup && activityGroup.activity_group_name,
        });
      }
      return;
    }
    state.activityGroups.selected = activityGroup;
    if (!programmatic) {
      trackBookingEvent('booking_service_filter_changed', {
        filter_action: 'add',
        service_name: activityGroup && activityGroup.activity_group_name,
      });
    }
    dispatch('setupActivities');
    // If a city is selected, filter the activities in this group to only those that have slots
    // in the city's allowed ClinicBuddy location_id(s).
    //
    // Prefer server-side filtering (single request) to avoid N client-side getAvailableTimes calls.
    // Fallback to probing if the endpoint isn't available (deploy mismatch).
    dispatch('filterActiveActivitiesForSelectedCityServerSide');
  },

  filterActiveActivitiesForSelectedCityServerSide({ state, dispatch }) {
    return new Promise((resolve) => {
      // No city selected => no filtering.
      if (!state.cities || !state.cities.selected) {
        resolve(state.activities.active);
        return;
      }
      // Need a selected group to compute within-group activity ids.
      if (!state.activityGroups || !state.activityGroups.selected || !state.activityGroups.selected.activity_group_id) {
        resolve(state.activities.active);
        return;
      }
      const city = state.cities.selected;
      const selectedLocationId =
        state.locations && state.locations.selected && state.locations.selected.id
          ? String(state.locations.selected.id)
          : null;
      const cityLocationIds = Array.isArray(city.locations)
        ? city.locations.map((l) => String(l.id)).filter(Boolean)
        : [];
      const locationIds = selectedLocationId ? [selectedLocationId] : cityLocationIds;
      if (!locationIds.length) {
        state.activities.active = [];
        resolve([]);
        return;
      }

      // If multiple locations exist for the selected city, do not attempt heavy filtering unless
      // the user has explicitly chosen a location (address). Otherwise it can time out and look
      // like "it keeps refetching".
      if (cityLocationIds.length > 1 && !selectedLocationId) {
        resolve(state.activities.active);
        return;
      }

      const groupId = String(state.activityGroups.selected.activity_group_id);
      const days = 90;

      // Prevent "wrong activities flash": clear list while we compute the allowed ids.
      const before = Array.isArray(state.activities.active) ? state.activities.active.slice() : [];
      state.activities.active = [];

      state.ui.loading.activitiesForCity = true;
      axios
        .post('/wp-json/nk/cb/getBookableActivityIdsForGroupForLocations/', {
          activity_group_id: groupId,
          location_ids: locationIds,
          days: days,
        }, { timeout: 20000 })
        .then((resp) => {
          const payload = resp && resp.data && resp.data.data ? resp.data.data : null;
          const ids = payload && Array.isArray(payload.activity_ids) ? payload.activity_ids : [];
          if (ids && ids.length > 0) {
            const allowed = new Set(ids.map((x) => String(x)));
            state.activities.active = (before || []).filter(
              (a) => a && a.activity_id && allowed.has(String(a.activity_id))
            );
          } else {
            // Server confirmed this group has no bookable activities for the selected location(s).
            // Remove it from cityBookableGroupIds so the UI hides it, and deselect.
            if (Array.isArray(state.activityGroups.cityBookableGroupIds)) {
              state.activityGroups.cityBookableGroupIds =
                state.activityGroups.cityBookableGroupIds.filter(
                  (id) => String(id) !== groupId
                );
            }
            state.activityGroups.selected = false;
            state.activities.active = before;
          }
          resolve(state.activities.active);
        })
        .catch(() => {
          // Deploy mismatch or error: fall back to client probing (correct but slower).
          // Restore the pre-filter list first so probing has something to work with.
          state.activities.active = before;
          dispatch('filterActiveActivitiesForSelectedCity')
            .then((r) => resolve(r))
            .catch(() => resolve(state.activities.active));
        })
        .finally(() => {
          state.ui.loading.activitiesForCity = false;
        });
    });
  },
  /* When a user selects an activity from the search results on the booking page via BookingModuleSearchActivity, set the activity group to false to make it a bit more intuitive for the user. */
  setActivityGroupFromSearchResult({ state, dispatch }, activityGroup) {
    //state.activityGroups.selected = activityGroup;
    state.activityGroups.selected = false;
  },
  toggleActivityGroup({ state, dispatch, getters }, activityGroup) {
    /* Don't allow the user to set this if we're loading times since it'll mess up the UI */
    if (getters.loading.times) {
      // return;
    }
    state.activities.selected = false;
    if (state.activityGroups.selected == activityGroup) {
      state.activityGroups.selected = false;
      trackBookingEvent('booking_service_filter_changed', {
        filter_action: 'remove',
        service_name: activityGroup && activityGroup.activity_group_name,
      });
      return;
    }
    dispatch('setActivityGroup', activityGroup);
  },
  setupActivities({ state }) {
    /* If we're running this function for the first time and we already have an activity_id parameter set, we don't want to reset that by setting state.activities.selected = false, so we just return this time */
    if (state.activities.initialLoad == false) {
      state.activities.selected = false;
    }

    state.activities.active = [];

    var activityGroup = state.activityGroups.selected;

    /* Loop through all activities and discard whichever are not active and do not match the selected activity group */
    for (var i in state.activities.available) {
      var activity = state.activities.available[i];
      if (activity.activity_active != '1') {
        continue;
      }
      if (activity.activity_hide_in_patient_portal == '1') {
        continue;
      }
      if (activity.activity_group_id == activityGroup.activity_group_id) {
        state.activities.active.push(activity);
      }
    }
  },

  filterActiveActivitiesForSelectedCity({ state }) {
    return new Promise(async (resolve) => {
      // NOTE:
      // `cityBookableIds` can be incomplete (warmup may stop after the first hit per group),
      // and in some environments it can be over-inclusive (expanded by group membership).
      // So we treat it as an optional optimization/hint, but correctness comes from probing.
      // No city selected => no filtering.
      if (!state.cities || !state.cities.selected) {
        resolve(state.activities.active);
        return;
      }

      const city = state.cities.selected;
      const locationIds = Array.isArray(city.locations)
        ? city.locations.map((l) => String(l.id)).filter(Boolean)
        : [];

      // No configured locations for city => show none.
      if (!locationIds.length) {
        state.activities.active = [];
        resolve([]);
        return;
      }

      const hintedAllowed =
        Array.isArray(state.activities.cityBookableIds) && state.activities.cityBookableIds.length > 0
          ? new Set(state.activities.cityBookableIds.map((x) => String(x)))
          : null;

      // Date window for "is this activity bookable in this city?"
      // Use a stable lookahead window (3 months from today) so the filter matches
      // what users expect when navigating the calendar (and avoids depending on
      // whatever week/month is currently active in the UI).
      const dateFrom = moment().format('YYYY-MM-DD');
      const dateTo = moment().add(3, 'months').format('YYYY-MM-DD');

      // Cancel any in-flight filter run.
      if (state.ui.loading.activitiesForCitySignal) {
        try {
          state.ui.loading.activitiesForCitySignal.cancel('cancel activitiesForCity');
        } catch (e) {}
      }
      const CancelToken = axios.CancelToken;
      const source = CancelToken.source();
      state.ui.loading.activitiesForCitySignal = source;
      state.ui.loading.activitiesForCity = true;

      const cityKey = String(city.id || city.slug || city.name || 'city');
      const locKey = locationIds.join(',');
      const windowKey = `${dateFrom}|${dateTo}`;
      const cacheVersion = 'v2';

      const cacheKey = (activityId) =>
        `${cacheVersion}|${cityKey}|${locKey}|${windowKey}|${String(activityId)}`;

      const hasSlotsForActivity = async (activityId) => {
        const key = cacheKey(activityId);
        if (Object.prototype.hasOwnProperty.call(state.activities.cityAvailabilityCache, key)) {
          return !!state.activities.cityAvailabilityCache[key];
        }

        for (let locId of locationIds) {
          try {
            const resp = await axios.post(
              '/wp-json/nk/cb/getAvailableTimes/',
              {
                activity_id: String(activityId),
                date_from: dateFrom,
                date_to: dateTo,
                location_id: String(locId),
              },
              { cancelToken: source.token }
            );
            const data = resp && resp.data && resp.data.data ? resp.data.data : null;
            // Explicitly treat non-OK statuses as 0
            if (data && data.status && String(data.status).toUpperCase() !== 'OK') {
              continue;
            }

            // Normalize rows: can be rows.row (array|object) or rows (array)
            let rows = null;
            if (data && data.rows) {
              rows = (data.rows.row !== undefined) ? data.rows.row : data.rows;
            }

            let count = 0;
            if (rows == null) {
              count = 0;
            } else if (Array.isArray(rows)) {
              count = rows.length;
            } else if (typeof rows === 'object') {
              // Only count as 1 if it looks like a time slot object
              count = (rows.date && rows.time) ? 1 : 0;
            } else {
              count = 0;
            }

            if (count > 0) {
              state.activities.cityAvailabilityCache[key] = true;
              return true;
            }
          } catch (e) {
            // If cancelled, stop immediately.
            if (axios.isCancel && axios.isCancel(e)) throw e;
            // Otherwise ignore per-location errors and try next.
          }
        }

        state.activities.cityAvailabilityCache[key] = false;
        return false;
      };

      const activities = state.activities.active || [];
      const filtered = [];

      // Concurrency-limited probing
      const concurrency = 3;
      let idx = 0;
      const worker = async () => {
        while (idx < activities.length) {
          const a = activities[idx++];
          if (!a || !a.activity_id) continue;
          // Optimization only: if backend provided a hint-list, skip probing activities outside it.
          // If the hint is over-inclusive, this doesn't harm correctness (we'll just probe more).
          if (hintedAllowed && !hintedAllowed.has(String(a.activity_id))) {
            continue;
          }
          const ok = await hasSlotsForActivity(a.activity_id);
          if (ok) filtered.push(a);
        }
      };

      try {
        await Promise.all(Array.from({ length: concurrency }, () => worker()));
        // Keep original ordering by activity_order
        state.activities.active = _.sortBy(filtered, (o) => parseInt(o.activity_order, 10));
      } catch (e) {
        // Cancelled: do nothing
      } finally {
        state.ui.loading.activitiesForCity = false;
        state.ui.loading.activitiesForCitySignal = null;
        resolve(state.activities.active);
      }
    });
  },

  // Used by the search component: filter an arbitrary list of activities by selected city availability.
  filterActivitiesByCityAvailability({ state, dispatch }, activities) {
    // Temporarily set active list to the passed list, run the same filter, then restore.
    // This keeps one source of truth for probing/caching.
    return new Promise(async (resolve) => {
      const prev = state.activities.active;
      state.activities.active = Array.isArray(activities) ? activities : [];
      await dispatch('filterActiveActivitiesForSelectedCity');
      const filtered = state.activities.active;
      state.activities.active = prev;
      resolve(filtered);
    });
  },
  /*
    Payload is either the activity itself (user clicked a treatment —
    tracked as funnel step 1) or { activity, programmatic: true } for
    page-load flows (PHP-set activity_id on treatment pages, URL preselect)
    which must NOT fire booking_service_selected — the spec's open funnel
    lets those users enter at step 2 instead.
  */
  setActivity({ state, dispatch }, payload) {
    const programmatic = !!(payload && payload.programmatic === true);
    const activity = programmatic ? payload.activity : payload;
    /* Don't let the user set the activity if we're loading times because this messes up the UI and can make times for a certain provider appear under the wrong headline in the calendar */
    if (state.ui.loading.times) {
      // return;
    }

    /*
        Set this to true so that we fetch more times the first time an activity is selected.
        Then, when the user navigates the calendar with the arrows, only load the necessary number of times on each navigation.
        */
    state.firstLoadForActivity = true;
    dispatch('setupActiveDateFromDate', {
      date: state.dates.current.niceDate,
    })
      .then(() => {
        state.providers.available = false;
        // Keep admin-driven city locations so we can map location_id -> address labels.
        // If we clear locations.available here, later mapping falls back to city name ("Göteborg")
        // even when multiple locations exist in the city.
        if (!state.cities || !state.cities.selected) {
          state.locations.available = false;
        }
        state.ui.selectingProvider = false;
        state.ui.selectingLocation = false;
        dispatch('setProvider', { provider: false, programmatic: true });
        // Do not reset location if a city is selected (city selection must persist across treatment selection)
        if (!state.cities || !state.cities.selected) {
        dispatch('setLocation', false);
        }

        state.selectedSlot = false;
        state.activities.selected = activity;

        if (activity && !programmatic) {
          /* Dedupe on id, not name — ClinicBuddy has distinct activities
             sharing a display name (per-city variants) and those must not
             flag each other as revisions. Name only as fallback. */
          trackBookingEvent(
            'booking_service_selected',
            funnelParams(state, 1),
            {
              funnel: true,
              dedupeValue: activity.activity_id || activity.activity_name,
            }
          );
        }

        dispatch('getAvailableTimes', {
          activityJustSet: true,
        });

        if (document.getElementById('book-time-dates')) {
          window.setTimeout(() => {
            state.vueInstance.$SmoothScroll(
              document.getElementById('book-time-dates'),
              500
            );
          }, 100);
        }

        dispatch('getProviders');
        dispatch('getLocations'); // Maybe
      })
      .catch(() => {});
  },
  /*
    Payload is either the provider value (user picked/cleared in the UI —
    tracked) or { provider, programmatic: true } for internal resets
    (activity selection, filter-open reset) which must NOT fire
    booking_practitioner_filter_applied.
  */
  setProvider({ state, dispatch, getters }, payload) {
    const programmatic = !!(payload && payload.programmatic === true);
    const provider = programmatic ? payload.provider : payload;
    console.log('state.providers.selected', state.providers.selected, provider);
    /* Only track a cleared filter if the user actually had one */
    const hadProvider = !!state.providers.selected;
    if (!programmatic) {
      if (provider) {
        trackBookingEvent('booking_practitioner_filter_applied', {
          practitioner_name: practitionerNameFromProvider(state, provider),
        });
      } else if (hadProvider) {
        trackBookingEvent('booking_practitioner_filter_applied', {
          practitioner_name: null,
        });
      }
    }
    state.providers.selected = provider;
    state.ui.selectingProvider = false;
    // Selecting a provider should jump to the next available time for that provider (same behavior as the button).
    // This makes provider selection useful even when the current week has no times.
    if (provider) {
      dispatch('getNextAvailableTime');
      return;
    }
    // Clearing provider -> just refresh filtered view for current week/month.
    dispatch('fillOutDates').catch(() => {});
  },
  setLocation({ state, dispatch, getters }, location) {
    console.log('state.locations.selected', state.locations.selected, location);
    if (!location) {
      state.locations.selected = false;
    } else if (state.locations.selected && state.locations.selected.id == location.id) {
      state.locations.selected = false;
    } else {
      state.locations.selected = location;
    }

    // Keep provider selection aligned with location selection:
    // if the selected provider has no times for this location/city window, clear it.
    if (
      state.providers &&
      state.providers.selected &&
      Array.isArray(getters.providersAvailableForSelection) &&
      getters.providersAvailableForSelection.length > 0 &&
      !getters.providersAvailableForSelection.includes(state.providers.selected)
    ) {
      state.providers.selected = false;
    }

    state.ui.selectingLocation = false;
    dispatch('fillOutDates')
      .then(() => {
        /* Check if there are not any available times for the current week. If so, navigate to the next available time */
        if (getters.noAvailableTimesForWeek) {
          dispatch('navigateToNextAvailableTime');
        }
      })
      .catch(() => {});
  },
  // Legacy entrypoint: keep name for compatibility, route to the fixed V2 implementation.
  getNextAvailableTime({ dispatch }) {
    return dispatch('getNextAvailableTimeV2');
  },

  /**
   * V2: A fresh, deterministic "next available" flow.
   * Goals:
   * - Never show an empty week due to cache/race conditions.
   * - Keep loading=true until the calendar has been filled (weekday.activeTimes populated).
   * - Prefer provider-specific next-available when provider metadata is available.
   */
  getNextAvailableTimeV2({ state, dispatch, getters }) {
    if (!state.activities || !state.activities.selected || !state.activities.selected.activity_id) {
      return Promise.resolve();
    }

    state.ui.loading.times = true;

    // Prefer currently selected location, otherwise use all locations for selected city (as "1;2;3").
    const cityLocationIds =
      state.cities &&
      state.cities.selected &&
      Array.isArray(state.cities.selected.locations)
        ? state.cities.selected.locations.map((l) => String(l.id)).filter(Boolean)
        : [];

    // UX + performance: if a city has multiple locations, require explicit selection before "next available".
    if (
      cityLocationIds.length > 1 &&
      !(state.locations && state.locations.selected && state.locations.selected.id)
    ) {
      state.ui.loading.times = false;
      return Promise.resolve();
    }
    const locationIdParam =
      state.locations && state.locations.selected && state.locations.selected.id
        ? String(state.locations.selected.id)
        : cityLocationIds.length
          ? cityLocationIds.join(";")
          : undefined;

    const runRequest = () => {
      const terapeutIdParam =
        state.providers && state.providers.selected && getters && getters.selectedProviderId
          ? String(getters.selectedProviderId)
          : undefined;

      return axios
        .get("/wp-json/nk/cb/getNextAvailableTime/", {
          params: {
            activity_id: state.activities.selected.activity_id,
            location_id: locationIdParam,
            terapeut_id: terapeutIdParam,
          },
          timeout: 20000,
        })
        .then((response) => {
          const row = response && response.data && response.data.data && response.data.data.rows
            ? response.data.data.rows.row
            : null;
          if (!row || !row.date) {
            state.availableTimes = [];
            return dispatch("setupAvailableTimes");
          }

          // IMPORTANT: bust cached windows for this activity so we don't reuse a stale empty/partial payload.
          const activityId = state.activities.selected.activity_id;
          if (Array.isArray(state.loadedTimes)) {
            state.loadedTimes = state.loadedTimes.filter((x) => x && x.activity_id !== activityId);
          }

          return dispatch("setupActiveDateFromDate", { date: row.date })
            .then(() => {
              // Force refresh times for the new window; we already suppressed auto-jump in this flow.
              return dispatch("getAvailableTimes", { dontNavigateToNextAvailableTime: true });
            });
        })
        .catch(() => {
          // Keep UI responsive even on failure.
          state.availableTimes = [];
          return dispatch("setupAvailableTimes");
        })
        ;
    };

    // If provider is selected but meta isn't loaded yet, load it first so terapeut_id can be used.
    if (state.providers && state.providers.selected && !state.providers.metaLoaded) {
      return dispatch("getProvidersMeta").then(() => runRequest());
    }
    return runRequest();
  },
  setupActiveDateFromDate({ state, dispatch }, payload) {
    return new Promise((resolve) => {
      let date = moment(payload.date);
      if (state.ui.calendarViewType == 'month') {
        date = date.startOf('month');
      }
      let activeDateObject = {
        date: date,
        day: date.date(),
        week: date.week(),
        month: date.month(),
        year: date.isoWeekYear(),
        niceDate: date.format('YYYY-MM-DD'),
        monthName: date.format('MMMM'),
        monthShortName: date.format('MMM'),
        monthLongName: date.format('MMMM'),
        weekStartDate: date.startOf('isoWeek').format('YYYY-MM-DD'),
        weekEndDate: date.endOf('isoWeek').format('YYYY-MM-DD'),
        slots: [],
        checkBookingsFromDate: date.startOf('isoWeek').format('YYYY-MM-DD'),
        checkBookingsToDate: date.endOf('isoWeek').format('YYYY-MM-DD'),
        previousWeek: date.clone().subtract(1, 'w').week(),
        nextWeek: date.clone().add(1, 'w').week(),
        previousMonth: date.clone().subtract(1, 'months').format('MMMM'),
        previousMonthShortName: date
          .clone()
          .subtract(1, 'months')
          .format('MMM'),
        nextMonth: date.clone().add(1, 'months').format('MMMM'),
        nextMonthShortName: date.clone().add(1, 'months').format('MMM'),
        monthDates: [],
      };

      /* If we're set to display weeks or if we're NOT on a single treatment page, check bookings for one week ahead or if this is NOT the first load for this activity */
      if (
        state.ui.calendarViewType == 'week' &&
        !window.singleTreatmentPage &&
        !state.firstLoadForActivity
      ) {
        activeDateObject.checkBookingsToDate = date
          .clone()
          .endOf('isoWeek')
          .format('YYYY-MM-DD');
        /* Else check bookings for one month ahead, and also one week before and after the month, to fill out the calendar correctly */
      } else {
        activeDateObject.checkBookingsFromDate = date
          .clone()
          .startOf('month')
          .subtract(1, 'w')
          .format('YYYY-MM-DD');
        activeDateObject.checkBookingsToDate = date
          .clone()
          .endOf('isoWeek')
          .format('YYYY-MM-DD');
      }
      /* On a single treatment page, show times four weeks ahead from today. Stopping at the end of
      the current week left the page with no times at all late in the week. The server fetches the
      four weeks in parallel (chunk_days in getAvailableTimes), so this stays about as fast as one week. */
      if (window.singleTreatmentPage) {
        activeDateObject.checkBookingsToDate = moment()
          .add(27, 'days')
          .format('YYYY-MM-DD');
      }
      state.firstLoadForActivity = false;
      console.log(9);
      state.dates.active = activeDateObject;
      dispatch('setupWeekdaysForWeek', activeDateObject.week);
      resolve();
    });
  },
  getAvailableTimes({ state, dispatch }, payload) {
    if (!state.activities.selected.activity_id) {
      return Promise.resolve();
    }

    console.log(1);

    /* Hide the showGetNextAvailableTimeButton */
    state.ui.showGetNextAvailableTimeButton = false;
    /* If we should increase the checkBookingsToDate, do it */
    if (payload) {
      console.log(2);
      if (payload.increasedCheckBookingsToDate) {
        /*
        Set the Check Bookings From Date to the 'checkBookingsToDate' variable, since ClinicBuddy only lets us 
        fetch available times like 3 months ahead of time
        */
        state.dates.active.checkBookingsFromDate = moment(
          state.dates.active.checkBookingsToDate
        ).format('YYYY-MM-DD');

        state.dates.active.checkBookingsToDate = moment(
          state.dates.active.checkBookingsToDate
        )
          .add(3, 'M')
          .format('YYYY-MM-DD');
      }
    }

    state.ui.loading.times = true;

    console.log(3);
    /* Keep track on if we should navigate to the next available time. The default behaviour is 'true' */
    var doNavigateToNextAvailableTime = true;
    if (payload) {
      if (payload.dontNavigateToNextAvailableTime) {
        doNavigateToNextAvailableTime = false;
      }
    }

    /* Check if available times are loaded. If so, don't make a request to the API */
    const chain = dispatch('availableTimesAreLoaded')
      .then(() => {
        console.log(4);
        /* Available times are loaded */
        state.ui.loading.times = false;
        /*
        If the user has just set a new activity, i.e. clicked an activity button to fetch times,
        navigate to the next available time
        */
        if (
          payload &&
          payload.activityJustSet &&
          doNavigateToNextAvailableTime
        ) {
          console.log(5);
          dispatch('navigateToNextAvailableTime');
        }
        return true;
      })
      .catch(() => {
        console.log(6);
        if (state.ui.loading.signal) {
          state.ui.loading.signal.cancel();
        }
        const CancelToken = axios.CancelToken;
        const source = CancelToken.source();
        state.ui.loading.signal = source;
        /* Available times are NOT loaded */
        let postData = {
          activity_id: state.activities.selected.activity_id,
          date_from:
            moment() >= moment(state.dates.active.checkBookingsFromDate)
              ? moment().format('YYYY-MM-DD')
              : state.dates.active.checkBookingsFromDate,
          date_to: state.dates.active.checkBookingsToDate,
        };
        // ClinicBuddy slows down with the length of the range, so let the server fetch the
        // treatment page's four weeks one week at a time, in parallel.
        if (window.singleTreatmentPage) {
          postData.chunk_days = 7;
        }

        // Only force location_id when user explicitly selected a location.
        // When only a city is selected, we fetch and then filter client-side to
        // the city’s enabled locations (admin-driven).
        if (state.locations && state.locations.selected && state.locations.selected.id) {
          postData.location_id = String(state.locations.selected.id);
        }
        // If the selected city only has ONE enabled ClinicBuddy location, always force location_id
        // to avoid fetching a huge "all locations" payload (which can stall and time out).
        const cityAllowedIds =
          state.cities &&
          state.cities.selected &&
          Array.isArray(state.cities.selected.locations)
            ? state.cities.selected.locations.map((l) => String(l.id)).filter(Boolean)
            : [];
        if (!postData.location_id && cityAllowedIds.length === 1) {
          postData.location_id = cityAllowedIds[0];
        }

        return axios
          .post('/wp-json/nk/cb/getAvailableTimes/', postData, {
            cancelToken: source.token,
            timeout: 20000,
          })
          .then((response) => {
            if (response.data.data.rows == undefined) {
              state.availableTimes = [];

              /*
                            If there are no available times, 
                            set showGetNextAvailableTimeButton to true to allow the user to 
                            do a search for available times where the checkBookingsToDate 
                            is set further ahead in the future
                            */
              if (!state.availableTimes.length) {
                state.ui.showGetNextAvailableTimeButton = true;
              }

              /* Store an empty array as the available times */
              dispatch('storeAvailableTimes', []);
              /* Set up the available times to make the calendar blank */
              return dispatch('setupAvailableTimes');
            }

            state.availableTimes = [];

            /* If we only received a single time from the ClinicBuddy API, it will be delivered as an object instead of as an array.
                        If so, convert it to an array containing the time slot object so that we can handle it correctly. */
            if (!Array.isArray(response.data.data.rows.row)) {
              response.data.data.rows.row = [response.data.data.rows.row];
            }

            if (window.singleTreatmentPage) {
              // Build location_id → city/address map from cities.available
              const locMap = {};
              if (state.cities && Array.isArray(state.cities.available)) {
                state.cities.available.forEach((city) => {
                  if (Array.isArray(city.locations)) {
                    city.locations.forEach((loc) => {
                      locMap[String(loc.id)] = {
                        city: city.name || '',
                        address: loc.name || loc.short_name || '',
                        short_name: loc.short_name || loc.name || '',
                      };
                    });
                  }
                });
              }

              _.forEach(response.data.data.rows.row, (time, key) => {
                const loc = locMap[String(time.location_id)];
                const cityLabel = loc ? loc.city : '';
                const addressLabel = loc ? loc.address : `Plats ${time.location_id}`;
                state.availableTimes.push({
                  ...time,
                  location_name: addressLabel,
                  location_short_name: cityLabel || addressLabel,
                });
              });
            } else {
              let times = [];
              for (let i in response.data.data.rows.row) {
                let time = response.data.data.rows.row[i];
                //if (time.terapeut_id == 8 || time.terapeut_id == 12) {
                /*
                                if (time.terapeut_id == 12) {
                                  continue;
                                }
                                */
                times.push(time);
              }
              const locationLabelById = (state.locations && Array.isArray(state.locations.available))
                ? state.locations.available.reduce((acc, l) => {
                    acc[String(l.id)] = l;
                    return acc;
                  }, {})
                : {};

              state.availableTimes = times
                .map((time) => {
                  const loc = locationLabelById[String(time.location_id)];
                  const label = loc ? (loc.name || loc.short_name) : null;
                  const cityLabel =
                    state.cities && state.cities.selected && state.cities.selected.name
                      ? state.cities.selected.name
                      : 'Okänd plats';
                  return {
                ...time,
                    location_name: label || cityLabel,
                    location_short_name: label || cityLabel,
                  };
                })
                // Filter to enabled locations for the selected city (if set)
                .filter((t) => {
                  if (!cityAllowedIds.length) return true;
                  return cityAllowedIds.includes(String(t.location_id));
                });
              console.log(
                'state.availableTimesstate.availableTimes',
                state.availableTimes
              );
              dispatch('storeAvailableTimes', times);
              return dispatch('setupAvailableTimes');
            }

            /* Check if available times is an object with only one time (eg the length is 'undefined'). If so, we need to convert it into an array */
            if (state.availableTimes.length == undefined) {
              console.log('ARE WE IN HERE?');
              var clonedAvailableTimes = Object.assign(
                {},
                state.availableTimes
              );
              state.availableTimes = [];
              state.availableTimes.push(clonedAvailableTimes);
              console.log(
                'AVAILABLETIMESSSS clonedAvailableTimes',
                clonedAvailableTimes
              );
            }

            return dispatch('setupAvailableTimes').then(() => {
              if (doNavigateToNextAvailableTime) {
                dispatch('navigateToNextAvailableTime');
              }
            });
          })
          .catch((err) => {
            // IMPORTANT: Always clear the spinner, even when requests are cancelled due to rapid navigation.
            if (axios.isCancel && axios.isCancel(err)) {
              return;
            }
            state.availableTimes = [];
            state.ui.showGetNextAvailableTimeButton = true;
          })
          .finally(() => {
            // Only clear if this is still the active request.
            if (state.ui.loading.signal === source) {
              state.ui.loading.times = false;
              state.ui.loading.signal = null;
            } else {
              state.ui.loading.times = false;
            }
          });
      });

    /* Spec step 2: the calendar has rendered available times for the selected
       service. Attempted on EVERY load — the initial week can be empty, and a
       user who finds times only after navigating weeks must still enter the
       funnel at step 2. skipIfFired keeps re-renders quiet: only a fresh
       activityJustSet re-selection may repeat the event (flagged is_revision);
       week/month navigation itself is tracked as booking_week_navigated. */
    chain
      .then(() => {
        if (
          state.availableTimes &&
          state.availableTimes.length &&
          state.activities.selected
        ) {
          trackBookingEvent('booking_timeslot_viewed', funnelParams(state, 2), {
            funnel: true,
            /* id, not name — same reasoning as booking_service_selected */
            dedupeValue:
              state.activities.selected.activity_id ||
              state.activities.selected.activity_name,
            skipIfFired: !(payload && payload.activityJustSet),
          });
        }
      })
      .catch(() => {});

    return chain;
  },
  setupAvailableTimes({ state, dispatch }) {
    /* Set up available times */
    // Ensure the timeslots show CITY label in the calendar tiles before we render the calendar.
    // (Gray booking summary box uses the selectedSlot mapping separately.)
    return Promise.resolve()
      .then(() => dispatch('formatLocations'))
      .then(() => dispatch('fillOutCalendarWithAvailableTimes'))
      .then(() => {
        state.ui.loading.times = false;
        state.activities.initialLoad = false;
        dispatch('getLocations');
      })
      .catch(() => {
        // Ensure spinner is cleared even if something unexpected happens while filling dates.
        state.ui.loading.times = false;
      });
  },
  getProviders({ state, dispatch }) {
    state.ui.loading.providers = true;
    state.providers.loaded = false;

    // Prefer selected location; otherwise all locations for selected city (as "1;2;3")
    const cityLocationIds =
      state.cities &&
      state.cities.selected &&
      Array.isArray(state.cities.selected.locations)
        ? state.cities.selected.locations.map((l) => String(l.id)).filter(Boolean)
        : [];
    const locationIdParam =
      state.locations && state.locations.selected && state.locations.selected.id
        ? String(state.locations.selected.id)
        : cityLocationIds.length
          ? cityLocationIds.join(';')
          : undefined;

    axios
      .get('/wp-json/nk/cb/getProvidersByActivity', {
        params: {
          activity_id: state.activities.selected.activity_id,
          location_id: locationIdParam,
        },
      })
      .then((response) => {
        state.providers.available = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        state.providers.loaded = true;
        state.ui.loading.providers = false;
        // Load provider meta in the background (so next-available can be provider-specific).
        dispatch('getProvidersMeta');
        dispatch('reorderProviders');
      })
      .catch(() => {
        state.providers.available = [];
        state.providers.loaded = true;
        state.ui.loading.providers = false;
      });
  },

  /**
   * Fetch provider metadata (terapeut_id + terapeut_sign) so we can call next-available for a selected provider.
   * Safe to call multiple times.
   */
  getProvidersMeta({ state }) {
    return new Promise((resolve) => {
      if (state.providers && state.providers.metaLoaded) {
        resolve(state.providers.metaRows || []);
        return;
      }
      if (state.providers && state.providers.metaLoading) {
        resolve(state.providers.metaRows || []);
        return;
      }
      state.providers.metaLoading = true;
      axios
        .get('/wp-json/nk/cb/getProviders/', { timeout: 20000 })
        .then((resp) => {
          const payload = resp && resp.data && resp.data.data ? resp.data.data : null;
          let rows = payload && payload.rows ? payload.rows.row : null;
          if (rows && rows.terapeut_id) {
            rows = [rows];
          }
          state.providers.metaRows = Array.isArray(rows) ? rows : [];
          state.providers.metaLoaded = true;
          resolve(state.providers.metaRows);
        })
        .catch(() => {
          state.providers.metaRows = [];
          state.providers.metaLoaded = false;
          resolve([]);
        })
        .finally(() => {
          state.providers.metaLoading = false;
        });
      });
  },
  formatLocations({ state, dispatch }) {
    const apply = () => {
      const cityLabel =
        state.cities && state.cities.selected && state.cities.selected.name
          ? state.cities.selected.name
          : '';

      const cityByLocId = {};
      if (state.cities && Array.isArray(state.cities.available)) {
        state.cities.available.forEach((city) => {
          if (Array.isArray(city.locations)) {
            city.locations.forEach((loc) => {
              cityByLocId[String(loc.id)] = city.name || '';
            });
          }
        });
      }

      let temp = state.availableTimes.map((time) => {
        const id = time && time.location_id ? String(time.location_id) : null;
        const resolvedCity = id ? (cityByLocId[id] || '') : '';
        const label = cityLabel || resolvedCity || time.location_short_name || time.location_name || (id ? `Plats ${id}` : '');
        return {
          ...time,
          location_name: label,
          location_short_name: label,
        };
      });
      state.availableTimes = temp;


      dispatch('storeAvailableTimes', temp);
    };

    // Wait for cities to load before resolving location labels (avoids race condition
    // where getAvailableTimes finishes before getAllAvailableCities).
    if (state.cities && state.cities.loaded) {
      apply();
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      let attempts = 0;
      const poll = setInterval(() => {
        attempts++;
        if ((state.cities && state.cities.loaded) || attempts > 50) {
          clearInterval(poll);
          apply();
          resolve();
        }
      }, 100);
    });
  },
  getLocations({ state, dispatch }) {
    // If locations are already configured from city selection (admin-driven), keep them.
    if (state.locations && Array.isArray(state.locations.available) && state.locations.available.length) {
      return;
    }
    let temp = [];
    if (state.availableTimes && state.availableTimes.length > 0) {
      temp = state.availableTimes
        .map((item) => {
          if (!item.location_id) return null;
          const label = item.location_name || item.location_short_name || `Plats ${item.location_id}`;
            return {
            id: String(item.location_id),
            name: label,
            short_name: label,
            };
        })
        .filter((x) => !!x);
      temp = temp.reduce((unique, o) => {
        if (!unique.some((obj) => String(obj.id) === String(o.id))) {
          unique.push(o);
        }
        return unique;
      }, []);
      state.locations.available = temp.sort((a, b) => Number(a.id) - Number(b.id));
    } else {
      state.locations.available = [];
    }
  },
  reorderProviders({ state }) {
    /* Define an array of the providers I want to move. This should be defined backwards, so that the last person in the array is the one that will be first when the function is done running */
    var elementsToMove = [
      'Malin Brinde Aukt. Hudterapeut',
      'Emma Kansell Aukt. Hudterapeut',
      'Carolina Ryberg Aukt. Hudterapeut',
      'Alexandra Lindbergh Aukt. Hudterapeut',
      'Denise Fa Injektionssjuksköterska',
      'Danielle Fa Injektionssjuksköterska',
      'Hamid Natghian Leg. Läkare. Specialist i plastikkirurgi',
      'Pehr Sommar Leg. Läkare Specialist i plastikkirurgi',
      'Said Saidian Leg. Läkare Specialist i plastikkirurgi',
      'Marcus Ehrström Leg. Läkare. Specialist i plastikkirurgi',
      'Magnus Kjelsberg Leg. Läkare. Specialist i plastikkirurgi',
      'Mattias Elmér Leg. Läkare. Specialist i plastikkirurgi',
      'Patrik Höijer Leg. Läkare. Specialist i plastikkirurgi',
    ];

    for (var i in elementsToMove) {
      var element = elementsToMove[i];
      if (state.providers.available.indexOf(element) > 0) {
        state.providers.available.splice(
          state.providers.available.indexOf(element),
          1
        );
        state.providers.available.unshift(element);
      }
    }
  },
  setupActiveDates({ state, dispatch }, direction) {
    if (state.ui.loading.times == true) {
      return;
    }

    if (direction == 'reset') {
      dispatch('setupDefaultDates', true);
      return;
    }

    if (document.getElementById('book-time-dates')) {
      window.setTimeout(() => {
        state.vueInstance.$SmoothScroll(
          document.getElementById('book-time-dates'),
          500
        );
      }, 100);
    }

    /* Get the type of calendar that the user is vieweing to subtract and add weeks or months */
    let type = 'w';
    if (state.ui.calendarViewType == 'month') {
      type = 'months';
    }

    if (direction == 'back') {
      state.dates.active.date = state.dates.active.date.subtract(1, type);
    }

    if (direction == 'forward') {
      state.dates.active.date = state.dates.active.date.add(1, type);
    }

    dispatch('setupActiveDateFromDate', {
      date: state.dates.active.date.format('YYYY-MM-DD'),
    }).then(() => {
      if (direction == 'forward' || direction == 'back') {
        trackBookingEvent('booking_week_navigated', {
          direction: direction == 'forward' ? 'next' : 'prev',
          week_number: state.dates.active.week,
          year: state.dates.active.year,
        });
      }
      var payload = {
        dontNavigateToNextAvailableTime: true, // Don't trigger navigation to the next available time because it causes the user to be thrown back into the calendar.
      };
      dispatch('getAvailableTimes', payload);
    });
  },
  setSelectedSlot({ state, dispatch }, slot) {
    /* Don't allow a new slot to be set if the user is currently doing a booking, i.e. a BankID sign */
    if (state.ui.loading.doingBooking) {
      return;
    }

    /* Clear the booking message when a slot is selected so that we don't display a potential old booking error message when the user selects a new slot */
    state.booking.message = false;

    /* Capture the previously selected slot before it's overwritten, for booking_timeslot_changed */
    const previousSlot =
      state.selectedSlot && state.selectedSlot.date
        ? { date: state.selectedSlot.date, time: state.selectedSlot.time }
        : null;

    dispatch('removeBodyClass');
    // IMPORTANT:
    // `slot` is a reference from `weekday.activeTimes` / `monthDate.activeTimes`.
    // We must NOT mutate it, otherwise the calendar tiles will change (e.g. city label becomes address).
    // Clone the slot for the booking summary box instead.
    state.selectedSlot = slot ? { ...slot } : slot;

    // In booking summary (gray info box), show the correct location label (usually a street address),
    // based on the selected city/location configuration (cached) when available.
    const slotLocationId = slot && slot.location_id ? String(slot.location_id) : null;
    const locationFromSelection =
      state.locations &&
      state.locations.selected &&
      String(state.locations.selected.id) === String(slotLocationId)
        ? state.locations.selected
        : null;
    const locationFromList =
      !locationFromSelection &&
      slotLocationId &&
      state.locations &&
      Array.isArray(state.locations.available)
        ? state.locations.available.find((l) => String(l.id) === String(slotLocationId))
        : null;
    // Treatment pages: locations.available may be empty, look up from cities.available instead
    let locationFromCities = null;
    if (!locationFromSelection && !locationFromList && slotLocationId && state.cities && Array.isArray(state.cities.available)) {
      for (const city of state.cities.available) {
        if (Array.isArray(city.locations)) {
          const match = city.locations.find((l) => String(l.id) === String(slotLocationId));
          if (match) {
            locationFromCities = match;
            break;
          }
        }
      }
    }
    const mapped = locationFromSelection || locationFromList || locationFromCities;
    const fallbackCity =
      state.cities && state.cities.selected && state.cities.selected.name
        ? state.cities.selected.name
        : '';

    state.selectedSlot.location_name =
      (mapped && (mapped.name || mapped.short_name)) ||
      slot.location_name ||
      fallbackCity ||
      (slotLocationId ? `Plats ${slotLocationId}` : '');
    state.selectedSlot.location_short_name =
      (mapped && (mapped.short_name || mapped.name)) ||
      slot.location_short_name ||
      slot.location_name ||
      fallbackCity ||
      (slotLocationId ? `Plats ${slotLocationId}` : '');

    if (slot == false) {
      return;
    }

    const practitioner = splitPractitioner(slot.terapeut_sign || '');
    trackBookingEvent(
      'booking_timeslot_selected',
      {
        ...funnelParams(state, 3),
        practitioner_name: practitioner.practitioner_name,
        practitioner_title: practitioner.practitioner_title,
        timeslot_date: slot.date,
        timeslot_time: slot.time,
        clinic_location: state.selectedSlot.location_name,
      },
      { funnel: true, dedupeValue: slot.date + ' ' + slot.time }
    );

    if (
      previousSlot &&
      (previousSlot.date !== slot.date || previousSlot.time !== slot.time)
    ) {
      trackBookingEvent('booking_timeslot_changed', {
        previous_date: previousSlot.date,
        previous_time: previousSlot.time,
        new_date: slot.date,
        new_time: slot.time,
      });
    }

    /* Spec step 4: the booking form renders v-if="selectedSlot", so it becomes visible here.
       Tracked in the store rather than PatientForm.vue — the component is mounted twice
       (desktop inline + mobile modal) and would double-fire. */
    trackBookingEvent('booking_form_viewed', funnelParams(state, 4), {
      funnel: true,
      dedupeValue: slot.date + ' ' + slot.time,
    });

    /* Focus on the first input field in the booking module so that the user can enter its name IF we're not on Internet Explorer */
    if (!document.body.classList.contains('ms-ie')) {
      window.setTimeout(() => {
        if (document.getElementById('patient-firstname')) {
          document.getElementById('patient-firstname').focus();
        }
      }, 650);
    }

    if (document.getElementById('book-time-form-anchor')) {
      window.setTimeout(() => {
        state.vueInstance.$SmoothScroll(
          document.getElementById('book-time-form-anchor'),
          500
        );
      }, 100);
    }

    if (
      document.getElementById('book-time-activity-form') &&
      !state.ui.didSwipe
    ) {
      window.setTimeout(() => {
        state.vueInstance.$SmoothScroll(
          document.getElementById('book-time-activity-form'),
          500
        );
      }, 100);
    }
  },
  toggleBodyClass() {
    var body = document.getElementsByTagName('BODY')[0];
    body.classList.toggle('mobile-menu-toggled');
  },

  removeBodyClass() {
    var body = document.getElementsByTagName('BODY')[0];
    body.classList.remove('mobile-menu-toggled');
  },

  addBodyClass() {
    var body = document.getElementsByTagName('BODY')[0];
    body.classList.add('mobile-menu-toggled');
  },
  setModalState({ state, dispatch }, value) {
    /* Set the modal state. If the value is 0, the modal should not be shown and we'll remove the body class. If it is above 1, we'll add the body class */

    if (value > 0) {
      dispatch('addBodyClass');
      state.ui.modalState = value;
    } else {
      dispatch('removeBodyClass');
      state.ui.modalState = 0;
    }
  },
  handleSwipe({ state }, event) {
    //event.preventDefault();
    /* Set didSwipe to true, then reset to false after 100 ms. This is a workaround to distinguish swipes and clicks in the slick slider when doing bookings on a single treatment page, like "Bröstförstoring" */
    state.ui.didSwipe = true;
    window.setTimeout(() => {
      state.ui.didSwipe = false;
    }, 100);
  },
  /* Prepare new booking when one has already been made */
  prepareNewBooking({ state, dispatch }) {
    state.booking.message = false;
    state.booking.timeStart = false;
    state.booking.timeEnd = false;
    state.booking.activityName = false;
    state.booking.completed = false;
    /* The previous booking's server-resolved city must not survive into this
       one. Slots whose location_id is missing from cities.available are a real
       case (clinics with booking disabled still return times — hence
       setSelectedSlot's `Plats ${id}` fallback), and resolveBookingCity would
       then fall through to the stale value and attribute the new booking to
       the city booked a moment ago. */
    state.booking.cityFromServer = null;
    state.selectedSlot = false;
    dispatch('setModalState', false);
    state.loadedTimes = [];

    var payload = {
      dontNavigateToNextAvailableTime: true, // Don't trigger navigation to the next available time because it causes the user to be thrown back into the calendar.
    };
    dispatch('getAvailableTimes', payload);

    if (document.getElementById('book-time-dates')) {
      window.setTimeout(() => {
        state.vueInstance.$SmoothScroll(
          document.getElementById('book-time-dates'),
          500
        );
      }, 100);
    }
  },
  shouldDeselectTimeslot({ state, getters }) {
    if (getters.loading.times) {
      return;
    }

    /* If an activity is not selected, also deselect time slot. This is done to prevent a mobile user from seeing a time slot as selected when no activity is selected */
    if (state.activities.selected == false) {
      state.selectedSlot = false;
    }
  },
  setBankIDLocation({ state }, location) {
    state.ui.signingBankIDOnOtherDevice = false;
    state.ui.signingBankIDOnSameDevice = false;
    if (location) {
      if (location == 'sameDevice') {
        state.bankIDLocation = 'sameDevice';
        state.ui.signingBankIDOnSameDevice = true;
      }
      if (location == 'otherDevice') {
        state.bankIDLocation = 'otherDevice';
        state.ui.signingBankIDOnOtherDevice = true;
      }
    }
  },

  /* Trigger a BankID sign based booking. */
  startBankIDBooking({ state, dispatch, getters }, bankIDLocation) {
    if (!getters.formIsValid) {
      /* The formIsValid getter just recomputed state.ui.formErrors — report what blocked this submit attempt */
      trackFormFieldErrors(state);
      return;
    }
    trackBookingEvent(
      'booking_form_submitted',
      {
        ...funnelParams(state, 5),
        has_consented_gdpr: !!state.patient.gdprConsent,
        has_consented_marketing: !!state.patient.newsletterSignup,
      },
      {
        funnel: true,
        dedupeValue:
          (state.selectedSlot &&
            state.selectedSlot.date + ' ' + state.selectedSlot.time) ||
          '',
      }
    );
    dispatch('trackBookingButtonClick', { mode: bankIDLocation || '', source: 'startBankIDBooking' });
    dispatch('setBankIDLocation', bankIDLocation);
    dispatch('testAddBooking', bankIDLocation);
  },

  /* Test if we can add the booking. This is run before we actually send the booking to the backend and trigger the BankID sign */
  testAddBooking({ state, dispatch, getters }) {
    if (!getters.formIsValid) {
      return;
    }

    state.booking.message = false;
    var timeStart =
      state.selectedSlot.date + ' ' + state.selectedSlot.time + ':00';
    /* Adjust the treatment length manually if needed */
    var duration = getters.getTreatmentLength;
    var timeEnd = moment(
      state.selectedSlot.date + ' ' + state.selectedSlot.time
    )
      .add(duration, 'minutes')
      .format('YYYY-MM-DD HH:mm:ss');
    timeStart = timeStart.toString();

    state.ui.loading.doingBooking = true;

    var bookingData = {
      firstName: getters.toTitleCase(state.patient.firstName),
      lastName: getters.toTitleCase(state.patient.lastName),
      ssn: state.patient.ssn,
      email: state.patient.email.toLowerCase(),
      phone: state.patient.phone,
      terapeutId: state.selectedSlot.terapeut_id,
      activityId: state.activities.selected.activity_id,
      bookingColor: state.activities.selected.activity_standard_color,
      timeStart: timeStart,
      timeEnd: timeEnd,
      newsletterSignup: state.patient.newsletterSignup,
    };

    /* Add the room ID and location ID if those parameters exist on the slot */
    if (state.selectedSlot.room_id != undefined) {
      bookingData.roomId = state.selectedSlot.room_id;
    }

    if (state.selectedSlot.location_id != undefined) {
      bookingData.locationId = state.selectedSlot.location_id;
    }

    axios
      .post('/wp-json/nk/cb/testAddBooking/', bookingData)
      .then((response) => {
        if (response.data.data.error == true) {
          state.ui.loading.doingBooking = false;
          state.booking.message = response.data.data.message;
          /* If we received an error, clear all the stored available times */
          state.loadedTimes = [];
          dispatch('setBankIDLocation', false);
          return;
        }
        if (response.data.data.success) {
          //dispatch('sendBooking');
          dispatch('initiateBankIDAuth');
        }
      })
      .catch((error) => {
        state.ui.loading.doingBooking = false;
        state.booking.message = 'Något gick fel. Försök igen.';
        /* Clear all state containing information of where the user is doing a BankID sign. */
        dispatch('setBankIDLocation', false);
      });
  },
  cancelBankIDOrder({ state, dispatch, getters }) {
    axios
      .get(
        '/wp-json/nk/cb/cancelBankIDOrder/?orderRef=' + state.booking.orderRef
      )
      .then((response) => {
        if (response.data.data.success) {
          trackBookingEvent('booking_bankid_cancelled', {
            reason: 'page_button',
          });
          clearTimeout(state.collectBankIDResponseTimeout);
          state.ui.loading.doingBooking = false;
          state.booking.message = response.data.data.message;
          state.booking.autostartToken = false;
          state.booking.orderRef = false;
          dispatch('setBankIDLocation', false);
        }
        if (response.data.data.error) {
          state.booking.message = response.data.data.message;
        }
      })
      .catch((error) => {
        state.booking.message =
          'Något gick fel när din bokning skulle avbrytas.';
      });
  },
  collectBankID({ state, dispatch, getters }) {
    axios
      .get('/wp-json/nk/cb/collectBankID/?orderRef=' + state.booking.orderRef)
      .then((response) => {
        if (
          response.data.data &&
          response.data.data.status === 'pending' &&
          response.data.data.hint === 'outstandingTransaction'
        ) {
          state.booking.message =
            'Du har redan en pågående BankID‑signering. Öppna BankID‑appen och slutför eller avbryt signeringen och försök igen. ' +
            'Om det fastnar kan du klicka på “Avbryt BankID‑signering” här på sidan och sedan starta om bokningen.';
          // Slow down polling a bit while waiting for the user to take action.
          clearTimeout(state.collectBankIDResponseTimeout);
          state.collectBankIDResponseTimeout = setTimeout(() => {
            dispatch('collectBankID');
          }, 5000);
          return;
        }

        if (
          response.data.data &&
          response.data.data.status === 'pending' &&
          response.data.data.hint === 'userSign'
        ) {
          clearTimeout(state.qrTimeout);
          state.booking.qrCode = '';
          state.booking.waitingForSign = true;
          state.booking.nbrRuns = 0;
          // state.ui.loading.doingBooking = false;
          // state.booking.message = response.data.data.message;
          // state.booking.autostartToken = false;
          // state.booking.orderRef = false;
          state.collectBankIDResponseTimeout = setTimeout(() => {
            dispatch('collectBankID');
          }, 1000);

          // Stop authenticate call
          // Hide QR
          // Show new div with info about authenticate
          // SHow timer?
          // Add button for restart
        }
        if (response.data.data && response.data.data.status === 'failed') {
          /* Covers the user cancelling INSIDE the BankID app as well as sign
             timeouts — the backend doesn't pass BankID's hintCode through, so
             they can't be told apart client-side. orderRef guard: a page-button
             cancel (cancelBankIDOrder) clears orderRef and already tracked
             reason page_button — an in-flight poll that comes back failed
             after that must not double-fire. */
          if (state.booking.orderRef) {
            trackBookingEvent('booking_bankid_cancelled', {
              reason: 'bankid_sign_failed',
            });
          }
          clearTimeout(state.collectBankIDResponseTimeout);
          clearTimeout(state.qrTimeout);
          state.ui.loading.doingBooking = false;
          state.booking.message = response.data.data.message;
          state.booking.autostartToken = false;
          state.booking.orderRef = false;
          state.booking.nbrOfRuns = 0;
          state.booking.waitingForSign = false;
        }
        if (response.data.success && response.data.status !== 'pending') {
          if (response.data.data && response.data.data.error) {
            return;
          }
          if (response.data.data && response.data.data.status === 'pending') {
            return;
          }
          clearTimeout(state.collectBankIDResponseTimeout);
          state.ui.loading.doingBooking = false;
          state.booking.message = response.data.message;
          state.booking.waitingForSign = false;
          state.booking.qrCode = '';
          state.booking.autostartToken = false;
          state.booking.orderRef = false;
          state.booking.nbrOfRuns = 0;
          state.booking.waitingForSign = false;

          if (response) {
            if (response.data) {
              if (response.data.data) {
                if (response.data.data.selectedActivity) {
                  state.activities.selected =
                    response.data.data.selectedActivity;
                }
                if (response.data.data.selectedSlot) {
                  state.selectedSlot = response.data.data.selectedSlot;
                }
                /* City the backend resolved from the order's location id. Only the
                   orderRef paths carry it, and only they need it: the store is fresh
                   in the tab BankID returned to, so nothing has selected a city and
                   the tracking city would otherwise be dropped. Read-only for
                   tracking — assigning cities.selected here would fire
                   RESET_SELECTIONS_FOR_CITY_CHANGE and wipe the booking. */
                if (response.data.data.city) {
                  state.booking.cityFromServer = response.data.data.city;
                }
              }
            }
          }

          /* Clear any potential error messages that might linger. */
          state.booking.message = false;
          state.booking.showBankIDInstructions = false;

          /* Clear all state containing information of where the user is doing a BankID sign. */
          dispatch('setBankIDLocation', false);

          state.ui.loading.doingBooking = false;
          state.booking.completed = true;
          state.ui.showConfirmationModal = true;

          state.vueInstance.$SmoothScroll(
            document.getElementById('book-time-form-anchor'),
            500
          );
          state.booking.activityName = state.activities.selected.activity_name;

          /* Push to Google Tag Manager and Facebook */
          //dataLayer.push({'event': 'gtm4wp.cbBookingMade'});
          //

          const consultationPrice = (() => {
            if (!state.infoBox) {
              return 0; // Default price if no infoBox
            }

            // Look for "Konsultationsavgift: X kr" pattern
            const priceMatch = state.infoBox.match(
              /Konsultationsavgift:\s*(\d+)\s*kr/i
            );
            if (priceMatch && priceMatch[1]) {
              return parseInt(priceMatch[1], 10);
            }

            return 0; // Default price if no match found
          })();

          // Generate a unique booking ID for tracking
          const bookingId =
            'booking_' +
              Date.now() +
              '_' +
              Math.random().toString(36).substr(2, 9);

          // Normalize city to a stable GA4-friendly slug (e.g. stockholm, goteborg, skovde).
          // Falls back to the slot's location when no city was ever selected —
          // see resolveBookingCity (tracking.js).
          const bookingCity = bookingCitySlug(state);

          // Push to dataLayer for GTM (client-side and server-side)
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'booking_completed',
            event_category: 'Booking',
            event_label: state.booking.activityName,
            consultation_price: consultationPrice,
            currency: 'SEK',
            booking_city: bookingCity,
            activity_id: state.activities.selected.activity_id,
            terapeut_id: state.selectedSlot.terapeut_id,
            // Additional parameters for enhanced tracking
            value: consultationPrice,
            treatment_name: state.booking.activityName,
            // Shared dedupe key for client-side Pixel and server-side CAPI (if configured in GTM).
            event_id: bookingId,
            booking_id: bookingId, // Add booking_id for Google Ads conversion tracking

            // Customer fields
            customer_email: state.patient.email
              ? state.patient.email.toLowerCase()
              : undefined,
            customer_phone: state.patient.phone || '',
            customer_first_name: getters.toTitleCase(
              state.patient.firstName || ''
            ),
            customer_last_name: getters.toTitleCase(
              state.patient.lastName || ''
            ),

            // Facebook parameters for server-side GTM
            fb_event_name: 'Lead',
            fb_custom_event_name: 'TreatmentBooking',
            fb_custom_parameters: {
              treatment_name: state.booking.activityName,
              consultation_price: consultationPrice,
              currency: 'SEK',
            },
            // Delta Projects parameters
            delta_projects_action: 'order',
            delta_projects_total_price: consultationPrice,
            delta_projects_currency: 'SEK',
            delta_projects_page_id: '$booking' + state.booking.activityName,
            from_actions: true,
          });

          /* Spec step 6 (conversion) — sent alongside the legacy booking_completed above.
             booking_id: hashed real ClinicBuddy id (spec: hashat/anonymiserat),
             deterministic so cross-path duplicates share the same id. No PII. */
          trackBookingConfirmed(state, {
            response: response,
            consultationPrice: consultationPrice,
          });

          /* Track that the booking is completed through our custom endpoint that communicates with the Facebook Conversion API */
          // dispatch('trackBookingComplete');

          state.booking.autostartToken = false;
          state.booking.orderRef = false;
          // state.ui.loading.doingBooking = true;
          // dispatch("setBankIDLocation", false);
        }
        if (response.data.data && response.data.data.error) {
          state.booking.message = response.data.data.message;
          state.booking.nbrOfRuns = 0;
          state.booking.waitingForSign = false;
        }
      })
      .catch((error) => {
        console.log('ERROR?', error);
        state.booking.message = 'Något gick fel när din bokning skulle skapas.';
      });
  },
  addQueryParameter({ state }, payload) {
    return new Promise((resolve, reject) => {
      const url = new URL(window.location.href);
      url.searchParams.set(payload.key, payload.value);
      window.history.pushState({}, '', url.toString());
      resolve();
      });
  },
  initiateBankIDAuth({ state, dispatch, getters }) {
    trackBookingEvent('booking_bankid_initiated');
    state.booking.autostartToken = false;
    state.booking.orderRef = false;
    state.orderRefQueryParameterLoaded = false;

    /* Set up the booking data */
    var timeStart =
      state.selectedSlot.date + ' ' + state.selectedSlot.time + ':00';
    /* Adjust the treatment length manually if needed */
    var duration = getters.getTreatmentLength;
    var timeEnd = moment(
      state.selectedSlot.date + ' ' + state.selectedSlot.time
    )
      .add(duration, 'minutes')
      .format('YYYY-MM-DD HH:mm:ss');
    timeStart = timeStart.toString();

    state.ui.loading.doingBooking = true;

    var bookingData = {
      firstName: getters.toTitleCase(state.patient.firstName),
      lastName: getters.toTitleCase(state.patient.lastName),
      ssn: state.patient.ssn,
      email: state.patient.email.toLowerCase(),
      phone: state.patient.phone,
      terapeutId: state.selectedSlot.terapeut_id,
      activityId: state.activities.selected.activity_id,
      bookingColor: state.activities.selected.activity_standard_color,
      timeStart: timeStart,
      timeEnd: timeEnd,
      newsletterSignup: state.patient.newsletterSignup,
      selectedActivity: state.activities.selected,
      selectedSlot: state.selectedSlot,
      // Persist current page URL (including restored UTM) through BankID hops for attribution/debugging.
      sourceUrl: window.location && window.location.href ? window.location.href : '',
    };

    /* Add the room ID and location ID if those parameters exist on the slot */
    if (state.selectedSlot.room_id != undefined) {
      bookingData.roomId = state.selectedSlot.room_id;
    }

    if (state.selectedSlot.location_id != undefined) {
      bookingData.locationId = state.selectedSlot.location_id;
    }

    axios
      .post('/wp-json/nk/cb/initiateBankIDAuth/', {
        ssn: state.patient.ssn,
        sameDevice: true,
        bookingData: bookingData,
      })
      .then((response) => {
        if (response.data.data.error == true) {
          state.ui.loading.doingBooking = false;
          state.booking.message = response.data.data.message;
          dispatch('setBankIDLocation', false);
          return;
        }
        if (response.data.data.success) {
          state.booking.autostartToken = response.data.data.autostartToken;
          state.booking.orderRef = response.data.data.orderRef;

          dispatch('collectBankIDResponseAndSendBooking');
          /* If we're on the same device, try to trigger the BankID app */
          /* Set the orderref query parameter, so that if the user is redirected to a new browser tab, new browser window or new browser entirely after their BankID signing, we can fetch the booking from the orderref and finish it, since the URL they are redirected to will now contain the query parameter with an orderref. */
          var queryParams = {
            key: 'orderref',
            value: state.booking.orderRef,
          };
          dispatch('addQueryParameter', queryParams)
            .then(() => {
              dispatch('autostartBankIDApp');
            })
            .catch(() => {});
        }
      })
      .catch((error) => {
        state.ui.loading.doingBooking = false;
        state.booking.message = 'Något gick fel. Försök igen.';
        /* Clear all state containing information of where the user is doing a BankID sign. */
        dispatch('setBankIDLocation', false);
      });
  },
  /* Poll for a BankID auth result. Also POST all the booking data here, so that we can make the booking if we collect a completed auth. */
  collectBankIDResponseAndSendBooking({ state, dispatch, getters }) {
    state.ui.loading.doingBooking = true;

    var data = {
      orderRef: state.booking.orderRef,
      // Helps backend keep campaign info even if Referer is missing/stripped.
      sourceUrl: window.location && window.location.href ? window.location.href : '',
    };

    axios
      .post('/wp-json/nk/cb/collectBankIDResponseAndSendBooking/', data)
      .then((response) => {
        state.booking.message = false;
        if (response.data.data.error == true) {
          /* status 'failed' = the BankID sign itself failed (user cancelled in
             the app, or timed out) — other error==true shapes are booking-API
             errors after a successful sign and must NOT count as cancelled.
             orderRef guard: a page-button cancel (cancelBankIDOrder) clears
             orderRef and already tracked reason page_button — an in-flight
             poll that comes back failed after that must not double-fire. */
          if (response.data.data.status === 'failed' && state.booking.orderRef) {
            trackBookingEvent('booking_bankid_cancelled', {
              reason: 'bankid_sign_failed',
            });
          }
          state.ui.loading.doingBooking = false;
          state.booking.message = response.data.data.message;
          /* If we received an error, clear all the stored available times */
          state.loadedTimes = [];
          /* Clear all state containing information of where the user is doing a BankID sign. */
          dispatch('setBankIDLocation', false);
          state.booking.autostartToken = false;
          state.booking.orderRef = false;
          return;
        }

        /* If the status is pending, try again in two seconds */
        if (response.data.data.status == 'pending') {
          const hint = response.data.data.hint;
          // Helpful UX: some BankID hint codes are actionable for the user.
          // outstandingTransaction means the user already has an active BankID signing session.
          if (hint === 'outstandingTransaction') {
            state.booking.message =
              'Du har redan en pågående BankID‑signering. Öppna BankID‑appen och slutför eller avbryt signeringen och försök igen. ' +
              'Om det fastnar kan du klicka på “Avbryt BankID‑signering” här på sidan och sedan starta om bokningen.';
          }

          // userSign means BankID is presenting the signing screen to the user.
          // Mark that we're waiting for sign so the autostart-instructions timer
          // (in autostartBankIDApp) knows BankID opened successfully and won't
          // show the misleading "couldn't open automatically" message.
          if (hint === 'userSign') {
            state.booking.waitingForSign = true;
            state.booking.showBankIDInstructions = false;
            state.booking.message = false;
          }

          // Avoid hammering BankID when we know the user must take action.
          const nextPollMs = hint === 'outstandingTransaction' ? 5000 : 2000;
          state.collectBankIDResponseTimeout = setTimeout(() => {
            dispatch('collectBankIDResponseAndSendBooking');
          }, nextPollMs);
          return;
        }

        /* If we made it this far, the booking is a success. Clear the timeout to collect a new response */
        clearTimeout(state.collectBankIDResponseTimeout);

        /* 
                If we received parameters about the selected slot and selected activity from our request, add them to the current state. 
                This is used to display proper confirmation messages for bookings that are triggered using a query parameter, e.g. 
                when the BankID app redirects the user to a new browser tab or new browser after authenticating with BankID. This allows us to display 
                the name of the activity, the date and time of the selected slot etc in the booking confirmation modal.
                */
        if (response) {
          if (response.data) {
            if (response.data.data) {
              if (response.data.data.selectedActivity) {
                state.activities.selected = response.data.data.selectedActivity;
              }
              if (response.data.data.selectedSlot) {
                state.selectedSlot = response.data.data.selectedSlot;
              }
              /* City the backend resolved from the order's location id. Only the
                 orderRef paths carry it, and only they need it: the store is fresh
                 in the tab BankID returned to, so nothing has selected a city and
                 the tracking city would otherwise be dropped. Read-only for
                 tracking — assigning cities.selected here would fire
                 RESET_SELECTIONS_FOR_CITY_CHANGE and wipe the booking. */
              if (response.data.data.city) {
                state.booking.cityFromServer = response.data.data.city;
              }
            }
          }
        }

        /* Clear any potential error messages that might linger. */
        state.booking.message = false;
        state.booking.showBankIDInstructions = false;

        /* Clear all state containing information of where the user is doing a BankID sign. */
        dispatch('setBankIDLocation', false);

        state.ui.loading.doingBooking = false;
        state.booking.completed = true;
        state.ui.showConfirmationModal = true;

        state.vueInstance.$SmoothScroll(
          document.getElementById('book-time-form-anchor'),
          500
        );
        /* This tab may be the one that PLACED the booking: on a new-tab BankID
           return whose original tab was discarded, isBookingSuccess is false
           and addBooking runs from here. That response carries no
           selectedActivity, so activities.selected is still false on a fresh
           /boka-tid/?orderref=… load — reading through it threw a TypeError
           into the outer .catch(), which showed the patient an error over a
           booking that had in fact succeeded and skipped every tracking push,
           booking_confirmed and its city included. addBooking hands back
           activityName for the same reason it hands back the city. */
        const selectedActivity = state.activities.selected || {};
        state.booking.activityName =
          selectedActivity.activity_name ||
          (response &&
            response.data &&
            response.data.data &&
            response.data.data.activityName) ||
          state.booking.activityName;

        /*
          IMPORTANT: This is the primary "successful booking" path for the BankID flow
          (initiated via initiateBankIDAuth -> collectBankIDResponseAndSendBooking).
          If we don't push to dataLayer here, most bookings will never be tracked in GA4/GTM.
        */
        const consultationPrice = (() => {
          if (!state.infoBox) {
            return 0; // Default price if no infoBox
          }

          // Look for "Konsultationsavgift: X kr" pattern
          const priceMatch = state.infoBox.match(
            /Konsultationsavgift:\s*(\d+)\s*kr/i
          );
          if (priceMatch && priceMatch[1]) {
            return parseInt(priceMatch[1], 10);
          }

          return 0; // Default price if no match found
        })();

        // Use ClinicBuddy booking_id if available, otherwise generate one
        const bookingId =
          (response &&
            response.data &&
            response.data.data &&
            response.data.data.booking_id) ||
          'booking_' +
            Date.now() +
            '_' +
            Math.random().toString(36).substr(2, 9);

        // Normalize city to a stable GA4-friendly slug (e.g. stockholm, goteborg, skovde).
        // Falls back to the slot's location when no city was ever selected —
        // see resolveBookingCity (tracking.js).
        const bookingCity = bookingCitySlug(state);

        // Push to dataLayer for GTM (client-side and server-side)
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'booking_completed',
          event_category: 'Booking',
          event_label: state.booking.activityName,
          consultation_price: consultationPrice,
          currency: 'SEK',
          booking_city: bookingCity,
          activity_id: selectedActivity.activity_id,
          terapeut_id: (state.selectedSlot || {}).terapeut_id,
          // Additional parameters for enhanced tracking
          value: consultationPrice,
          treatment_name: state.booking.activityName,
          // Shared dedupe key for client-side Pixel and server-side CAPI (if configured in GTM).
          event_id: bookingId,
          booking_id: bookingId, // Add booking_id for Google Ads conversion tracking

          // Customer fields
          customer_email: state.patient.email
            ? state.patient.email.toLowerCase()
            : undefined,
          customer_phone: state.patient.phone || '',
          customer_first_name: getters.toTitleCase(state.patient.firstName || ''),
          customer_last_name: getters.toTitleCase(state.patient.lastName || ''),

          // Facebook parameters for server-side GTM
          fb_event_name: 'Lead',
          fb_custom_event_name: 'TreatmentBooking',
          fb_custom_parameters: {
            treatment_name: state.booking.activityName,
            consultation_price: consultationPrice,
            currency: 'SEK',
          },
          // Delta Projects parameters
          delta_projects_action: 'order',
          delta_projects_total_price: consultationPrice,
          delta_projects_currency: 'SEK',
          delta_projects_page_id: '$booking' + state.booking.activityName,
          from_actions: true,
        });

        /* Spec step 6 (conversion) — sent alongside the legacy booking_completed above.
           booking_id: hashed real ClinicBuddy id (spec: hashat/anonymiserat),
           deterministic so cross-path duplicates share the same id. No PII. */
        trackBookingConfirmed(state, {
          response: response,
          consultationPrice: consultationPrice,
        });

        /* Track that the booking is completed through our custom endpoint that communicates with the Facebook Conversion API */
        // dispatch('trackBookingComplete');

        state.booking.autostartToken = false;
        state.booking.orderRef = false;
      })
      .catch((error) => {
        console.log('!ERROR!', error);
        state.ui.loading.doingBooking = false;
        state.booking.message =
          'Något gick fel när bokningen skulle slutföras. Du kan försöka igen.';

        state.booking.autostartToken = false;
        state.booking.orderRef = false;
        /* Clear all state containing information of where the user is doing a BankID sign. */
        dispatch('setBankIDLocation', false);
      });
  },
  autostartBankIDApp({ state, dispatch, getters }) {
    /* Autostart the BankID app. This is used after receiving a token */
    var autoStartUrl = getters.getAutostartUrl;
    if (autoStartUrl) {
      // Detect if we're in an in-app browser that might block bankid:// protocol
      var isInAppBrowser =
        /Instagram|FBAN|FBAV|FB_IAB|FBAN|FBAV|FBIOS|FB_IAB|Twitter|LinkedInApp|Line|WhatsApp|Telegram|Snapchat|TikTok|Pinterest|WeChat|QQ/i.test(
          navigator.userAgent
        );

      // Try to open BankID app with fallback for mobile browsers
      try {
        if (isInAppBrowser) {
          // For in-app browsers, show alternative booking options instead of trying BankID
          console.log(
            'Detected in-app browser, showing alternative booking options'
          );
          dispatch('showInAppBrowserBookingOptions');
          return; // Don't try to open BankID at all
        } else if (getters.isIosOrAndroid) {
          // For regular mobile browsers, try the iframe + direct approach
          var iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = autoStartUrl;
          document.body.appendChild(iframe);

          // Also try direct window.location as fallback
          setTimeout(() => {
            window.location.href = autoStartUrl;
          }, 100);

          // Clean up iframe after a short delay
          setTimeout(() => {
            if (iframe.parentNode) {
              iframe.parentNode.removeChild(iframe);
            }
          }, 2000);

          // Show instructions after a delay if autostart might have failed.
          // We wait 5 seconds to give the BankID polling time to detect a
          // 'userSign' hint (which sets waitingForSign = true). The polling
          // runs every 2 seconds, so by 5s we'll have 2-3 responses.
          // We also track wall-clock time to detect if the page was backgrounded
          // (i.e. the BankID app opened successfully and the user was signing).
          var autostartTimestamp = Date.now();
          setTimeout(() => {
            var elapsed = Date.now() - autostartTimestamp;
            // If significantly more real time passed than the timer duration,
            // the page was backgrounded – BankID likely opened fine.
            if (elapsed > 12000) {
              return;
            }
            if (!state.booking.completed && !state.booking.waitingForSign) {
              dispatch('showBankIDInstructions');
            }
          }, 5000);
        } else {
          // For desktop, use direct redirect
          window.location.href = autoStartUrl;
        }
      } catch (error) {
        console.warn('BankID autostart failed, showing instructions:', error);
        // If any error occurs, show instructions instead of trying direct redirect
        dispatch('showBankIDInstructions');
      }
    }
  },

  showBankIDInstructions({ state }) {
    /* Show instructions for manual BankID app opening */
    state.booking.showBankIDInstructions = true;
    state.booking.message =
      'BankID-appen kunde inte öppnas automatiskt. Klicka på länken nedan för att öppna BankID manuellt.';
  },

  hideBankIDInstructions({ state }) {
    /* Hide BankID instructions */
    state.booking.showBankIDInstructions = false;
    state.booking.message = false;
  },

  submitBookingWithoutBankID({ state, dispatch, getters }) {
    /* Submit booking without BankID authentication */
    state.ui.loading.doingBooking = true;
    state.booking.message = 'Skickar din bokning...';
    if (!getters.formIsValid) {
      /* Report validation errors but don't block — the server validates this path */
      trackFormFieldErrors(state);
    }
    trackBookingEvent(
      'booking_form_submitted',
      {
        ...funnelParams(state, 5),
        has_consented_gdpr: !!state.patient.gdprConsent,
        has_consented_marketing: !!state.patient.newsletterSignup,
      },
      {
        funnel: true,
        dedupeValue:
          (state.selectedSlot &&
            state.selectedSlot.date + ' ' + state.selectedSlot.time) ||
          '',
      }
    );
    dispatch('trackBookingButtonClick', { mode: 'no_bankid', source: 'submitBookingWithoutBankID' });

    // Build payload to match BankID flow (backend expects the same shape).
    var timeStart =
      state.selectedSlot.date + ' ' + state.selectedSlot.time + ':00';
    var duration = getters.getTreatmentLength;
    var timeEnd = moment(
      state.selectedSlot.date + ' ' + state.selectedSlot.time
    )
      .add(duration, 'minutes')
      .format('YYYY-MM-DD HH:mm:ss');
    timeStart = timeStart.toString();

    var bookingData = {
      firstName: getters.toTitleCase(state.patient.firstName || ''),
      lastName: getters.toTitleCase(state.patient.lastName || ''),
      ssn: state.patient.ssn,
      email: state.patient.email ? state.patient.email.toLowerCase() : '',
      phone: state.patient.phone,
      terapeutId: state.selectedSlot && state.selectedSlot.terapeut_id,
      activityId: state.activities.selected.activity_id,
      bookingColor: state.activities.selected.activity_standard_color,
      timeStart: timeStart,
      timeEnd: timeEnd,
      newsletterSignup: !!state.patient.newsletterSignup,
      gdprConsent: !!state.patient.gdprConsent,
      // Keep slotData for backend telemetry/debugging (no longer used for required booking fields).
      slotData: state.selectedSlot,
      // Include current page URL (with UTM if present/restored) for attribution/debugging.
      sourceUrl: window.location && window.location.href ? window.location.href : '',
    };

    if (state.selectedSlot && state.selectedSlot.room_id != undefined) {
      bookingData.roomId = state.selectedSlot.room_id;
    }
    if (state.selectedSlot && state.selectedSlot.location_id != undefined) {
      bookingData.locationId = state.selectedSlot.location_id;
    }

    axios
      .post('/wp-json/nk/cb/bookingWithoutBankID/', bookingData)
      .then((response) => {
        // WP REST helpers can return HTTP 200 with { success: false, data: ... }.
        // Treat that as an error so we don't show "success" when no booking was created.
        if (response && response.data && response.data.success === false) {
          const msg =
            (response.data.data && response.data.data.message) ||
            response.data.message ||
            'Ett fel uppstod vid bokningen. Försök igen eller kontakta oss.';
          throw new Error(msg);
        }
        state.ui.loading.doingBooking = false;
        state.booking.completed = true;
        state.booking.message =
          'Din bokning har skickats! Vi kommer att kontakta dig för bekräftelse.';
        state.ui.showConfirmationModal = true;

        state.vueInstance.$SmoothScroll(
          document.getElementById('book-time-form-anchor'),
          500
        );
        state.booking.activityName = state.activities.selected.activity_name;

        /* Enhanced tracking - same as BankID flow */
        var consultationPrice = 0;
        if (state.priceData.available) {
          var priceForActivity = state.priceData.available.find(
            (price) =>
              price.activity_id == state.activities.selected.activity_id
          );
          if (priceForActivity) {
            consultationPrice = priceForActivity.price;
          }
        }
        // Generate a unique booking ID for tracking
        const bookingId =
          'booking_' +
            Date.now() +
            '_' +
            Math.random().toString(36).substr(2, 9);

        // Normalize city to a stable GA4-friendly slug (e.g. stockholm, goteborg, skovde).
        // Falls back to the slot's location when no city was ever selected —
        // see resolveBookingCity (tracking.js).
        const bookingCity = bookingCitySlug(state);

        // Push to dataLayer for GTM (client-side and server-side)
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'booking_completed',
          event_category: 'Booking',
          event_label: state.booking.activityName,
          consultation_price: consultationPrice,
          currency: 'SEK',
          booking_city: bookingCity,
          activity_id: state.activities.selected.activity_id,
          terapeut_id: state.selectedSlot.terapeut_id,
          // Additional parameters for enhanced tracking
          value: consultationPrice,
          treatment_name: state.booking.activityName,
          // Shared dedupe key for client-side Pixel and server-side CAPI (if configured in GTM).
          event_id: bookingId,
          booking_id: bookingId, // Add booking_id for Google Ads conversion tracking

          // Customer fields
          customer_email: state.patient.email
            ? state.patient.email.toLowerCase()
            : undefined,
          customer_phone: state.patient.phone || '',
          customer_first_name: getters.toTitleCase(
            state.patient.firstName || ''
          ),
          customer_last_name: getters.toTitleCase(state.patient.lastName || ''),

          // Facebook parameters for server-side GTM
          fb_event_name: 'Lead',
          fb_custom_event_name: 'TreatmentBooking',
          fb_custom_parameters: {
            treatment_name: state.booking.activityName,
            consultation_price: consultationPrice,
            currency: 'SEK',
          },
          // Delta Projects parameters
          delta_projects_action: 'order',
          delta_projects_total_price: consultationPrice,
          delta_projects_currency: 'SEK',
          delta_projects_page_id: '$booking' + state.booking.activityName,
          from_actions: true,
        });

        /* Spec step 6 (conversion) — sent alongside the legacy booking_completed above.
           booking_id: hashed real ClinicBuddy id (spec: hashat/anonymiserat),
           deterministic so cross-path duplicates share the same id. No PII. */
        trackBookingConfirmed(state, {
          response: response,
          consultationPrice: consultationPrice,
        });
      })
      .catch((error) => {
        state.ui.loading.doingBooking = false;
        console.error('Booking submission error:', error);

        const errMsg =
          (error &&
            error.response &&
            error.response.data &&
            ((error.response.data.data && error.response.data.data.message) ||
              error.response.data.message)) ||
          (error && error.message) ||
          'Ett fel uppstod vid bokningen. Försök igen eller kontakta oss.';
        state.booking.message = errMsg;
      });
  },

  openBankIDManually({ state, getters }) {
    /* Copy BankID URL to clipboard and try to open it */
    var autoStartUrl = getters.getAutostartUrl;
    if (autoStartUrl) {
      // Try to copy to clipboard
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard
          .writeText(autoStartUrl)
          .then(() => {
            alert(
              'Länken har kopierats! Öppna nu din vanliga webbläsare (Safari, Chrome, etc.) och klistra in länken i adressfältet.'
            );
          })
          .catch(() => {
            // Fallback if clipboard fails
            this.fallbackCopyToClipboard(autoStartUrl);
          });
      } else {
        // Fallback for older browsers or non-secure contexts
        this.fallbackCopyToClipboard(autoStartUrl);
      }

      // Also try to open it directly as a fallback
      setTimeout(() => {
        window.location.href = autoStartUrl;
      }, 1000);
    }
  },

  fallbackCopyToClipboard(text) {
    /* Fallback method to copy text to clipboard */
    var textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      var successful = document.execCommand('copy');
      if (successful) {
        alert(
          'Länken har kopierats! Öppna nu din vanliga webbläsare (Safari, Chrome, etc.) och klistra in länken i adressfältet.'
        );
      } else {
        alert(
          'Kunde inte kopiera automatiskt. Länken är: ' +
            text +
            '\n\nKopiera denna länk och öppna i din vanliga webbläsare.'
        );
      }
    } catch (err) {
      alert(
        'Kunde inte kopiera automatiskt. Länken är: ' +
          text +
          '\n\nKopiera denna länk och öppna i din vanliga webbläsare.'
      );
    }

    document.body.removeChild(textArea);
  },

  initiateCheckout({ state, dispatch }) {
    /* Track the FB event InitiateCheckout when a user starts to enter their details in the checkout form */
    if (state.initiatedCheckout) {
      return;
    }
    // Meta Conversions API is disabled, and Meta Pixel events are handled in GTM (NKW-30)
    // dispatch('trackInitiateCheckout');

    // fbq('trackCustom', 'InitiateCheckout', {
    //   treatment_name: state.activities.selected.activity_name,
    // });

    state.initiatedCheckout = true;
  },
  toggleSelectingProvider({ state, dispatch }) {
    state.ui.selectingProvider = !state.ui.selectingProvider;
    /* Spec: booking_practitioner_filter_applied also fires when the user OPENS
       the Behandlare filter. Always with practitioner_name: null — opening
       CLEARS any active selection (the reset below, and the toggle button
       doubles as the clear-X when a provider is selected), so the outgoing
       practitioner's name would misreport a clear as an apply. The reset is
       programmatic so it doesn't double-fire as a second event. */
    if (state.ui.selectingProvider) {
      trackBookingEvent('booking_practitioner_filter_applied', {
        practitioner_name: null,
      });
    }
    if (state.ui.selectingProvider == false || state.providers.selected) {
      dispatch('setProvider', { provider: false, programmatic: true });
    }
  },
  toggleSelectingLocation({ state, dispatch }) {
    state.ui.selectingLocation = !state.ui.selectingLocation;
    if (state.ui.selectingLocation == false || state.locations.selected) {
      dispatch('setLocation', false);
    }
  },
  calendarViewType({ state, dispatch }, type) {
    state.ui.calendarViewType = type;
    let date = state.dates.active.date
      .clone()
      .startOf('month')
      .format('YYYY-MM-DD');

    /* If the date is in the past, set the date to today */
    if (
      state.dates.active.date
        .clone()
        .startOf('month')
        .isBefore(moment().format('YYYY-MM-DD'))
    ) {
      date = moment().format('YYYY-MM-DD');
    }
    dispatch('setupActiveDateFromDate', {
      date: date,
    }).then(() => {
      var payload = {
        dontNavigateToNextAvailableTime: true, // Don't trigger navigation to the next available time because it causes the user to be thrown back into the calendar.
      };
      dispatch('getAvailableTimes', payload);
    });
  },
  handleClickOnMonthCalendarSlot({ state, dispatch }, date) {
    let firstOfWeek = date.date.clone().startOf('week').format('YYYY-MM-DD');
    state.ui.calendarViewType = 'week';
    dispatch('setupActiveDateFromDate', {
      date: firstOfWeek,
    });

    /* Scroll to the correct date in the mobile booking modal */
    window.setTimeout(() => {
      let anchorElement = document.getElementById(
        'booking-calendar-week-mobile-date-anchor-' + date.niceDate
      );
      let modalBody = document.querySelector('.modal-time-slots-body');
      /*
            modalBody.scrollTop = anchorElement.offsetTop;
            */
      state.vueInstance.$SmoothScroll(anchorElement, 500, false, modalBody);
    }, 100);
  },
  /* Store available times for later use */
  storeAvailableTimes({ state }, times) {
    let timeStampInSeconds = Math.floor(Date.now() / 1000);
    let objectToStore = {
      activity_id: state.activities.selected.activity_id,
      date_from: state.dates.active.checkBookingsFromDate,
      date_to: state.dates.active.checkBookingsToDate,
      availableTimes: times,
      exipresAt: timeStampInSeconds + 300, // Set the stored object to be invalid in 300 seconds, i.e. 5 minutes. This is done to not cache times indefinately on the client and to fetch fresh times if needed.
    };
    state.loadedTimes.push(objectToStore);
  },

  /* 
    Function to check if the times for the given treatment and dates are already fetched by the user. 
    If so, use those times instead of reaching out to the ClinicBuddy API 
    */
  availableTimesAreLoaded({ state, dispatch }) {
    let timeStampInSeconds = Math.floor(Date.now() / 1000);
    return new Promise((resolve, reject) => {
      let activity_id = state.activities.selected.activity_id;
      let date_from = state.dates.active.checkBookingsFromDate;
      let date_to = state.dates.active.checkBookingsToDate;
      /* Check if the time is already stored */
      for (let i in state.loadedTimes) {
        let loadedTime = state.loadedTimes[i];

        /* Check if the loaded time has expired. If so, unset it and continue the loop */
        if (loadedTime.exipresAt < timeStampInSeconds) {
          continue;
        }

        if (loadedTime.activity_id == activity_id) {
          /* Check if the loaded time is for the same dates if the requested times are from dates WITHIN dates that we have times stored for */
          if (
            loadedTime.date_from == date_from ||
            moment(loadedTime.date_from).isBefore(date_from)
          ) {
            if (
              loadedTime.date_to == date_to ||
              moment(loadedTime.date_to).isAfter(date_to)
            ) {
              /* The time is stored. Set the available times to the stored times */
              state.availableTimes = loadedTime.availableTimes;
              dispatch('setupAvailableTimes')
                .then(() => resolve(true))
                .catch(() => resolve(true));
            }
          }
        }
      }
      reject();
    });
  },
  fakeCompleteBooking({ state, dispatch }) {
    state.ui.loading.doingBooking = false;
    state.booking.completed = true;
    state.ui.showConfirmationModal = true;
  },

  /* Track a completed booking in the Facebook Conversion API through our custom endpoint */
  // trackBookingComplete({ state }) {
  //   var sourceUrl = window.location.href;
  //   var email = state.patient.email.toLowerCase();
  //   var phone = state.patient.phone;
  //   axios
  //     .get(
  //       '/wp-json/nk/capi/bookingComplete/?sourceUrl=' +
  //         sourceUrl +
  //         '&treatmentName=' +
  //         state.booking.activityName +
  //         '&email=' +
  //         email +
  //         '&phone=' +
  //         phone
  //     )
  //     .then((response) => {})
  //     .catch((error) => {});
  // },

  /* Track the event InitiateCheckout in the Facebook Conversion API through our custom endpoint */
  trackInitiateCheckout({ state }) {
    var sourceUrl = window.location.href;
    axios
      .get(
        '/wp-json/nk/capi/initiateCheckout/?sourceUrl=' +
          sourceUrl +
          '&treatmentName=' +
          state.activities.selected.activity_name
      )
      .then((response) => {});
  },

  /* 
    Get any custom treatment durations for each treatment and provider from the WordPress backend. 
    When the user makes a booking, we check if the provider has a custom duration for this treatment and 
    sets the end time accordingly.
    */
  getCustomTreatmentDurations({ state }) {
    axios
      .get('/wp-json/nk/cb/getCustomTreatmentDurations/')
      .then((response) => {
        state.customTreatmentDurations = response.data.data;
      });
  },

  async getInfoBox({ state }) {
    try {
      const response = await axios.get('/wp-json/nk/cb/getInfoBox', {
        params: {
          activity_id: state.activities.selected.activity_id,
        },
      });

      // console.log(response.data.data.content); // Ensure the response structure matches your expectations

      state.infoBox = response.data.data.content;
    } catch (error) {
      state.infoBox = null;
      console.error('Error fetching infoBox:', error);
    }
  },

  authenticate({ state, dispatch, getters }) {
    var timeStart =
      state.selectedSlot.date + ' ' + state.selectedSlot.time + ':00';
    /* Adjust the treatment length manually if needed */
    var duration = getters.getTreatmentLength;
    var timeEnd = moment(
      state.selectedSlot.date + ' ' + state.selectedSlot.time
    )
      .add(duration, 'minutes')
      .format('YYYY-MM-DD HH:mm:ss');
    timeStart = timeStart.toString();

    var bookingData = {
      firstName: getters.toTitleCase(state.patient.firstName),
      lastName: getters.toTitleCase(state.patient.lastName),
      ssn: state.patient.ssn,
      email: state.patient.email.toLowerCase(),
      phone: state.patient.phone,
      terapeutId: state.selectedSlot.terapeut_id,
      activityId: state.activities.selected.activity_id,
      bookingColor: state.activities.selected.activity_standard_color,
      timeStart: timeStart,
      timeEnd: timeEnd,
      newsletterSignup: state.patient.newsletterSignup,
      selectedActivity: state.activities.selected,
      selectedSlot: state.selectedSlot,
    };

    /* Add the room ID and location ID if those parameters exist on the slot */
    if (state.selectedSlot.room_id != undefined) {
      bookingData.roomId = state.selectedSlot.room_id;
    }

    if (state.selectedSlot.location_id != undefined) {
      bookingData.locationId = state.selectedSlot.location_id;
    }

    if (state.booking.nbrRuns >= 25) {
      state.booking.nbrRuns = 0;
    }

    var postData = {
      nbrOfRuns: state.booking.nbrRuns,
      bookingData: bookingData,
    };
    if (state.booking.nbrRuns > 0 && state.booking.qrStartToken) {
      postData.qrData = {
        orderRef: state.booking.orderRef,
        qrStartToken: state.booking.qrStartToken,
        qrStartSecret: state.booking.qrStartSecret,
      };
    }
    axios
      .post('/wp-json/nk/cb/authenticate/', postData)
      .then((response) => {
        state.booking.nbrRuns = state.booking.nbrRuns + 1;
        if (response.data.data.error == true) {
          state.ui.loading.doingBooking = false;
          state.booking.message = response.data.data.message;
          dispatch('setBankIDLocation', false);
          return;
        }
        if (response.data.data.success) {
          if (response.data.data.qrStartToken) {
            state.booking.qrStartToken = response.data.data.qrStartToken;
            state.booking.qrStartSecret = response.data.data.qrStartSecret;
          }
          state.booking.qrCode =
            'https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=' +
            response.data.data.qrString;
          state.booking.orderRef = response.data.data.orderRef;
          state.qrTimeout = setTimeout(() => {
            if (!state.waitingForSign) {
              dispatch('authenticate');
            }
          }, 1000);
          dispatch('collectBankID');
        }
      })
      .catch((error) => {
        console.log('ERROR', error);
        state.ui.loading.doingBooking = false;
        state.booking.message = 'Något gick fel. Försök igen.';
        /* Clear all state containing information of where the user is doing a BankID sign. */
        dispatch('setBankIDLocation', false);
      });
  },
};
